// ModelViewer.jsx
import React, {
    Suspense,
    useState,
    useRef,
    useEffect,
    useMemo,
    useCallback,
} from 'react';
import { Canvas } from '@react-three/fiber';
import { GizmoHelper, GizmoViewcube, GizmoViewport } from '@react-three/drei';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import Datagrid from "../../components/Datagrid/Datagrid"
import {
    Box,
    Button,
    IconButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Tab,
    Tabs,
    Tooltip,
    Typography,
    Menu,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Badge,
    TextField,
    Checkbox,
    Card,
    CardContent,
    CardActionArea,
    alpha,
    Slider,
    RadioGroup,
    FormControlLabel,
    Radio,
    Switch,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    CircularProgress,
    useTheme,
} from '@mui/material';

import {
    Autorenew,
    CheckBox as CheckBoxIcon,
    Close,
    KeyboardDoubleArrowUp,
    Launch,
    LibraryAddCheck,
    Loop,
    NorthEast,
    QuestionMark,
    Search,
    ShoppingCart,
    Summarize,
    Menu as MenuIcon,
    Home as HomeIcon,
    OpenWith as OpenWithIcon,
    ZoomIn as ZoomInIcon,
    MoreVert as MoreVertIcon,
    Settings as SettingsIcon,
    ViewAgenda as ViewAgendaIcon,
    GridOn as GridOnIcon,
    TableChart as TableChartIcon,
    ExpandMore as ExpandMoreIcon,
    ChevronRight as ChevronRightIcon,
    CheckCircle,
    Block,
    Delete,
    OpenInFull,
    Info,
    InfoOutlined,
    SupportAgent,
    VerticalAlignTop,
} from '@mui/icons-material';

import LoadingBoxes from '../../components/LoadingBoxes/LoadingBoxes';
import { isEmpty, toCapitalCase } from '../../utilities/globalServiceUtils';
import { getMachineByNumberOrId } from '../../services/myMachineService';
import { useSite } from '../../context/siteContext';
import { useAuth } from '../../context/authContext';
import {
    getProductsByMachineId,
    normalizeMachineProductsResponse,
} from '../../services/machineCardService';
import { addModelCartItems } from '../../services/cartService';
import { ASSETS_BASE_URL, DEFAULT_AXIS_MODEL_VIEWER, RSPL_LIST, SERVER_BASE_URL } from '../../config';
import { useOptions } from "../../context/optionContext";
import { themeColors } from "../../utilities/themeColors";
import PageTitle from '../../components/PageTitle/PageTitle';
import ModelScene from '../../components/ModelScene/ModelScene';
import ModalWrapper from '../../components/ModalWrapper/ModalWrapper';
import ContactCCRForm from '../../components/ContactCCRForm/ContactCCRForm';
import Swal from "sweetalert2";
import { FiChevronsDown, FiChevronsUp } from "react-icons/fi";
import { BsTransparency } from "react-icons/bs";
import { PiDropSimpleFill } from "react-icons/pi";
import useBrandingAssets from "../../hooks/useBrandingAssets";
// ---------------------- helpers / constants ----------------------

const loadingMessages = [
    'Loading 3D Model...',
    'Loading Products and Machine Data...',
    'Rendering Model View...',
    'Model Loaded',
];

const MODEL_AXIS_CHOICES = ['X', 'Y', 'Z'];

const normalizeAxisKey = (value, fallback = 'Y') => {
    const raw = String(value || '').trim().toUpperCase();
    if (raw === 'X' || raw === 'X_UP') return 'X';
    if (raw === 'Y' || raw === 'Y_UP') return 'Y';
    if (raw === 'Z' || raw === 'Z_UP') return 'Z';
    return fallback;
};

const resolveModelOrientation = (optionKeys) => {
    const fallbackUp = normalizeAxisKey(DEFAULT_AXIS_MODEL_VIEWER || 'Y_UP', 'Y');
    const up = normalizeAxisKey(
        optionKeys?.modelViewerOrientationUp || optionKeys?.modelViewerUpAxis,
        fallbackUp
    );
    const left = normalizeAxisKey(
        optionKeys?.modelViewerOrientationLeft,
        up === 'X' ? 'Z' : 'X'
    );
    const right = normalizeAxisKey(optionKeys?.modelViewerOrientationRight, 'X');
    const isValid =
        MODEL_AXIS_CHOICES.includes(up) &&
        MODEL_AXIS_CHOICES.includes(left) &&
        MODEL_AXIS_CHOICES.includes(right) &&
        new Set([up, left, right]).size === 3;

    if (isValid) return { up, left, right };

    const fallbackLeft = up === 'X' ? 'Z' : 'X';
    const fallbackRight = MODEL_AXIS_CHOICES.find(
        (axis) => axis !== up && axis !== fallbackLeft
    ) || 'Y';
    return { up, left: fallbackLeft, right: fallbackRight };
};

const CAMERA_CORNER_CHOICES = new Set([
    'isometric',
    'front',
    'back',
    'left',
    'right',
    'top',
    'bottom',
]);

const normalizeCameraCorner = (value) => {
    const raw = String(value || '').trim().toLowerCase();
    return CAMERA_CORNER_CHOICES.has(raw) ? raw : 'isometric';
};


const resolveProductCardImageUrl = (product = {}) => {
    const raw = product?.asset_data?.images?.[0];
    if (raw) {
        const normalized = String(raw).trim();
        if (
            /^(https?:)?\/\//i.test(normalized) ||
            normalized.startsWith('data:')
        ) {
            return normalized;
        }
        const path = normalized.startsWith('/') ? normalized : `/${normalized}`;
        return `${ASSETS_BASE_URL}${path}`;
    }
    return `${ASSETS_BASE_URL}/ProductImages/TMB/${product?.product_number}.JPG`;
};

const resolvePartCardImageUrl = (data = {}, stockImageUrl = null) => {
    const explicit = data?.productImageURL;
    if (explicit) {
        const normalized = String(explicit).trim();
        if (
            /^(https?:)?\/\//i.test(normalized) ||
            normalized.startsWith('data:')
        ) {
            return normalized;
        }
        if (normalized.startsWith('/')) {
            return `${ASSETS_BASE_URL}${normalized}`;
        }
        return normalized;
    }

    const raw = data?.asset_data?.images?.[0];
    if (raw) {
        const normalized = String(raw).trim();
        if (
            /^(https?:)?\/\//i.test(normalized) ||
            normalized.startsWith('data:')
        ) {
            return normalized;
        }
        const path = normalized.startsWith('/') ? normalized : `/${normalized}`;
        return `${ASSETS_BASE_URL}${path}`;
    }

    const fallbackNumber =
        data?.part_number || data?.product_number || data?.partNumber;
    if (fallbackNumber) {
        return `${ASSETS_BASE_URL}/ProductImages/TMB/${fallbackNumber}.JPG`;
    }

    return stockImageUrl || `${ASSETS_BASE_URL}/stock.png`;
};

export const TabPanel = ({
    children,
    value,
    index,
    title,
    subtitle,
    headerActions = null,
    ...other
}) => {
    return (
        <Box
            {...other}
            sx={{
                height: '100%',
                px: 2,
                pb: 2,
                pt: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden', // keep outer non-scrollable
                flex: 1,
                minHeight: 0, // IMPORTANT: allow inner flex child to shrink
            }}
        >
            {value === index && (
                <>
                    {(title || headerActions) && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%',
                                mb: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    minWidth: 0,
                                }}
                            >
                                {title ? (
                                    <Typography fontWeight="bold" variant="body1" mb={0}>
                                        {title}
                                    </Typography>
                                ) : null}
                                {subtitle ? (
                                    <Typography variant="body2" fontSize="11px" mb={0}>
                                        {subtitle}
                                    </Typography>
                                ) : null}
                            </Box>
                            {headerActions ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        '& > *': {
                                            display: 'flex',
                                            alignItems: 'center',
                                        },
                                    }}
                                >
                                    {headerActions}
                                </Box>
                            ) : null}
                        </Box>
                    )}
                    {children}
                </>
            )}
        </Box>
    );
}

// Fixed SearchInput: split change vs submit
export const SearchInput = ({
    value,
    onChange,
    onSubmit,
    placeholder = 'Search...',
}) => {
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && onSubmit) {
            onSubmit();
        }
    };

    return (
        <Box>
            <TextField
                variant="outlined"
                placeholder={placeholder}
                fullWidth
                value={value}
                onChange={onChange}
                size="small"
                slotProps={{
                    input: {
                        endAdornment: (
                            <Box display="flex">
                                <IconButton
                                    size="small"
                                    sx={{ mr: '-2px' }}
                                    onClick={onSubmit}
                                >
                                    <Search fontSize="small" />
                                </IconButton>
                            </Box>
                        ),
                    },
                }}
                sx={{
                    '& .MuiInputBase-root': {
                        paddingRight: '2px',
                    },
                    '& .MuiOutlinedInput-root': {
                        backgroundColor: 'transparent',
                    },
                    '& .MuiInputBase-input::placeholder': {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    },
                    width: '100%',
                }}
                onKeyDown={handleKeyDown}
            />
        </Box>
    );
};

export const PartMenuButton = ({
    icon = null,
    buttonText = '',
    isActive = false,
    onClick = null,
    price = 0,
}) => {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
            }}
            onClick={onClick ?? undefined}
        >
            <Box sx={{ minHeight: '32px' }}>
                {icon && (
                    <IconButton
                        size="small"
                        color="inherit"
                        sx={{
                            color: isActive
                                ? (theme) => theme.palette.primary.main
                                : 'inherit',
                        }}
                    >
                        {icon}
                    </IconButton>
                )}
                {price ? (
                    <Typography
                        textWrap="nowrap"
                        variant="body1"
                        fontSize="14px"
                        mb={0}
                        fontWeight="bold"
                    >
                        {price}
                    </Typography>
                ) : null}
            </Box>
            <Typography
                textWrap="nowrap"
                variant="body2"
                fontSize="10px"
                sx={{
                    color: isActive
                        ? (theme) => theme.palette.primary.main
                        : 'inherit',
                }}
            >
                {buttonText}
            </Typography>
        </Box>
    );
};

export const PartMenu = ({
    onHeaderMouseDown,
    data,
    onClose,
    onShowParentClick,
    onIsolateClick,
    occurrenceToggle,
    onOccurrenceToggle,
    isInCart,
    isInRequest,
    addToCart,
    addToRequestList,
    enableModelActions = true,
}) => {
    const { t } = useTranslation();
    const canUse3DViewActions = data?.availableIn3D !== false;
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '60px',
                maxHeight: '125px',
                width: '100%',
                minWidth: '275px',
                maxWidth: '550px',
                borderRadius: '10px',
                boxShadow: '0 0 6px 1px rgba(0,0,0,0.1)',
                overflow: 'hidden',
                background: (theme) => theme.palette.background.paper,
                p: 1.25,
                userSelect: 'none',
            }}
        >
            {/* HEADER = drag handle */}
            <Box
                sx={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'grab',
                }}
                onMouseDown={onHeaderMouseDown}
            >
                <Box sx={{ width: '100%', flex: 1 }}>
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            columnGap: 1,
                        }}
                    >
                        <Typography
                            fontWeight="bold"
                            variant="body1"
                            mb={0}
                            noWrap
                            sx={{ maxWidth: "260px", textOverflow: "ellipsis", overflow: "hidden" }}
                        >
                            {data?.part_name || "Selected Part"}
                        </Typography>

                        {data?.url && !isEmpty(data?.url) && (
                            <Launch
                                sx={{ fontSize: '13px', cursor: 'pointer' }}
                                onClick={() => {
                                    window.open(data?.url, "_blank", "noopener,noreferrer");
                                }}
                            />
                        )}
                    </Box>
                    {data?.isOrderable ? (
                        <Typography
                            variant="body2"
                            fontSize="11px"
                            mb={1}
                            color="text.secondary"
                            noWrap
                            sx={{ maxWidth: "260px", textOverflow: "ellipsis", overflow: "hidden" }}
                        >
                            {t('UI_TEXT.PART')}: {data.part_number} | Part Type: {data.part_type}
                        </Typography>


                    ) : (
                        <Typography
                            variant="body2"
                            fontSize="11px"
                            mb={1}
                            color="text.secondary"
                        >
                            Non-Orderable Part
                        </Typography>
                    )}

                </Box>
                <Tooltip title={t("UI_TEXT.TOOLTIP_CLOSE")}>
                    <IconButton size="small" onClick={onClose}>
                        <Close />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* CONTENT */}
            <Box
                sx={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: 2,
                    columnGap: 2,
                }}
            >
                {/* ORDERABLE UI */}
                {data?.isOrderable ? (
                    <>
                        {data.price && data.purchasable ? (
                            <>

                                <PartMenuButton price={data.price} buttonText="Price" />
                                <PartMenuButton
                                    icon={<ShoppingCart />}
                                    buttonText={t('BUTTONS.ADD_TO_CART')}
                                    isActive={isInCart(data.part_number)}
                                    onClick={() => {
                                        if (isInCart(data.part_number)) return;
                                        const occurrenceQty =
                                            occurrenceToggle && data?.occurrenceCount
                                                ? data.occurrenceCount
                                                : 1;
                                        addToCart({ ...data, qty: occurrenceQty });
                                    }}
                                />
                            </>
                        ) : (<>

                            <PartMenuButton
                                icon={<Summarize />}
                                buttonText="Add to Request"
                                    isActive={isInRequest(data.part_number)}
                                    onClick={() => !isInRequest(data.part_number) && addToRequestList(data)}
                                />
                        </>)}


                    </>
                ) : (
                    /* NON-ORDERABLE → SHOW NOTHING HERE */
                    <></>
                )}


                {enableModelActions && (
                    <>
                        {canUse3DViewActions && onShowParentClick && (
                            <PartMenuButton
                                icon={<KeyboardDoubleArrowUp />}
                                buttonText="Show Parent"
                                onClick={onShowParentClick}
                            />
                        )}
                        {canUse3DViewActions && onIsolateClick && (
                            <PartMenuButton
                                icon={<NorthEast />}
                                buttonText="Isolate"
                                onClick={onIsolateClick}
                            />
                        )}
                        {data?.hasMultipleOccurrences && (
                            <PartMenuButton
                                icon={
                                    occurrenceToggle ? (
                                        <CheckBoxIcon />
                                    ) : (
                                        <LibraryAddCheck />
                                    )
                                }
                                buttonText={
                                    occurrenceToggle
                                        ? "Select Current"
                                        : "Select All Occurrences"
                                }
                                onClick={onOccurrenceToggle}
                                isActive={occurrenceToggle}
                            />
                        )}
                    </>
                )}

            </Box>
        </Box>
    );
};

// Multiple parts menu – appears after "Select Visible"
export const MultiplePartsMenu = ({
    count,
    items = [],
    onClose,
    onAddToCart,
    findMatchingProductForPart,
    getSitePrice,
    machineId,
    onHeaderMouseDown,
}) => {
    const { t } = useTranslation();
    const normalized = (value) => String(value ?? "").trim().toLowerCase();

    const uniqueItems = [];
    const seenPartNumbers = new Set();

    items.forEach((part, idx) => {
        const label =
            part?.assemblyNode?.name ||
            part?.name ||
            `Part ${idx + 1}`;

        const matchedProduct = findMatchingProductForPart(
            label,
            part?.assemblyNode?.productRef
        );

        const displayName =
            matchedProduct?.product_name ||
            part?.part_name ||
            label;
        const displayNumber =
            matchedProduct?.product_number ||
            part?.part_number ||
            part?.product_number ||
            "N/A";
        const dedupeKey = normalized(displayNumber);
        if (seenPartNumbers.has(dedupeKey)) return;
        seenPartNumbers.add(dedupeKey);

        const formattedPrice = matchedProduct
            ? getSitePrice(matchedProduct)
            : null;

        uniqueItems.push({
            key: `${dedupeKey}-${idx}`,
            part,
            cardData: {
                part_name: displayName,
                part_number: displayNumber,
                productImageURL: matchedProduct?.productImageURL ?? null,
                url:
                    matchedProduct?.product_id
                        ? `/machines/${machineId}/${matchedProduct.product_id}`
                        : "",
                purchasable: matchedProduct?.purchasable ?? false,
                price: formattedPrice,
                availableIn3D: true,
            },
        });
    });

    const uniqueCount = uniqueItems.length || count || 0;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '120px',
                maxHeight: '450px',
                width: '100%',
                minWidth: '275px',
                maxWidth: '480px',
                borderRadius: '10px',
                boxShadow: '0 0 6px 1px rgba(0,0,0,0.1)',
                overflow: 'hidden',
                background: (theme) => theme.palette.background.paper,
                p: 1.5,
                userSelect: 'none',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                    cursor: 'grab',
                }}
                onMouseDown={onHeaderMouseDown}
            >
                <Box>
                    <Typography fontWeight="bold" variant="body1" mb={0}>
                        Multiple Parts Selected
                    </Typography>
                    <Typography
                        variant="body2"
                        fontSize="11px"
                        color="text.secondary"
                    >
                        {uniqueCount} unique parts selected
                    </Typography>
                </Box>
                <Tooltip title={t("UI_TEXT.TOOLTIP_CLOSE")}>
                    <IconButton size="small" onClick={onClose}>
                        <Close />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* LIST AREA */}
            <Box
                sx={{
                    flex: 1,
                    overflowY: "auto",
                    maxHeight: "400px",   // UPDATED
                    pr: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                }}
            >

                {uniqueItems.length === 0 ? (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textAlign: "center", mt: 2 }}
                    >
                        No parts found.
                    </Typography>
                ) : (
                    uniqueItems.map((item) => {
                        return (
                            <PartCard
                                key={item.key}
                                data={item.cardData}
                                isActive={false}
                            />
                        );
                    })

                )}
            </Box>

            {/* ACTIONS */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    columnGap: 1,
                    mt: 2,
                }}
            >
                <Button variant="text" onClick={onClose}>
                    {t("BUTTON.CLOSE")}
                </Button>
                <Button variant="contained" onClick={onAddToCart} startIcon={<ShoppingCart />}>
                    Add All to Cart
                </Button>
            </Box>
        </Box>
    );
};

export const PartCard = ({ data, isActive, warning, showWarning = false }) => {
    const { t } = useTranslation();
    const { stockImageUrl } = useBrandingAssets();
    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: '450px',
                display: 'flex',
                alignItems: 'center',
                py: 1,
                px: 1,
                columnGap: 1.5,



                borderRadius: "8px",
                background: isActive
                    ? (theme) => alpha(theme.palette.primary.main, 0.2)
                    : warning
                        ? (theme) => alpha(theme.palette.warning.main, 0.2)
                        : "transparent",

                "&:hover": {
                    background: (theme) => alpha(theme.palette.action.hover, 0.1)
                }
            }}
        >
            <Box
                sx={{
                    height: '45px',
                    width: '45px',
                    borderRadius: '8px',
                    boxShadow: '0 0 6px 1px rgba(0,0,0,0.1)',
                    overflow: 'hidden',
                }}
            >
                <img
                    src={resolvePartCardImageUrl(data, stockImageUrl)}
                    alt={data?.part_name || data?.part_number || "Part image"}
                    onError={(e) => {
                        e.currentTarget.src = stockImageUrl || `${ASSETS_BASE_URL}/stock.png`;
                    }}
                    height="100%"
                    width="100%"
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",      // important
                        display: "block"         // removes inline gaps
                    }}
                />

            </Box>

            <Box
                sx={{
                    flex: 1,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    rowGap: 0,
                }}
            >
                {/* PART NAME */}
                <Tooltip title={data?.part_name || ""} placement="top">
                    <Box sx={{ display: "flex", alignItems: "center", columnGap: 0.5, maxWidth: "240px" }}>
                        <Typography
                            variant="body1"
                            fontWeight="bold"
                            noWrap
                            sx={{
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                                maxWidth: "220px",
                                cursor: "default"
                            }}
                        >
                            {data?.part_name}
                        </Typography>
                        {data?.url && !isEmpty(data?.url) && (
                            <Launch
                                sx={{ fontSize: '13px', cursor: 'pointer', flexShrink: 0 }}
                                onClick={() => {
                                    window.open(data?.url, "_blank", "noopener,noreferrer");
                                }}
                            />
                        )}
                    </Box>
                </Tooltip>



                {/* PART NUMBER */}
                <Tooltip title={data?.part_number || ""} placement="top">
                    <Typography
                        variant="body1"
                        fontSize="11px"
                        color="text.secondary"
                        noWrap
                        sx={{
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            maxWidth: "240px",
                            cursor: "default"
                        }}
                    >
                        {t('UI_TEXT.PART')}: {data?.part_number}
                    </Typography>
                </Tooltip>

                {showWarning && <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 0.25 }}>

                    <Typography
                        variant="body1"
                        fontSize={"10px"}
                        color='text.secondary'
                        noWrap
                        sx={{
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            maxWidth: "240px", // adjust if needed
                            cursor: "default"
                        }}
                    >
                        {data?.availableIn3D ? (
                            t('GENERAL.AVAILABLE_IN_3D')
                        ) : (
                            t('GENERAL.NOT_AVAILABLE_IN_3D')
                        )}
                    </Typography>
                </Box>}

            </Box>

            {data?.purchasable && data?.price ? (
                <Typography variant="body1" color="primary" fontWeight="bold" noWrap>
                    {data.price}
                </Typography>
            ) : null}
        </Box>
    );
};

const MemoPartCard = React.memo(PartCard);


// ---------- Assembly helpers ----------

function buildAssemblyIndex(root) {
    if (!root) return null;

    const nodeById = new Map();
    const parentById = new Map();
    const objectToNodeId = new Map(); // object uuid -> one nodeId (first match)
    const productRefToNodeIds = new Map(); // productRef -> [nodeId...]
    let counter = 0;

    const traverse = (node, parentId = null) => {
        const id = `n-${counter++}`;
        node._id = id;
        nodeById.set(id, node);

        if (parentId !== null) {
            parentById.set(id, parentId);
        }

        if (node.productRef !== null && node.productRef !== undefined) {
            const key = String(node.productRef);
            if (!productRefToNodeIds.has(key)) {
                productRefToNodeIds.set(key, []);
            }
            productRefToNodeIds.get(key).push(id);
        }

        if (Array.isArray(node.objects)) {
            node.objects.forEach((obj) => {
                if (obj && obj.uuid && !objectToNodeId.has(obj.uuid)) {
                    objectToNodeId.set(obj.uuid, id);
                }
            });
        }

        if (Array.isArray(node.children)) {
            node.children.forEach((child) => traverse(child, id));
        }
    };

    traverse(root, null);
    const rootId = root._id;

    return { nodeById, parentById, rootId, objectToNodeId, productRefToNodeIds };
}

// Filter tree by search text (case-insensitive)
function filterTreeBySearch(node, searchText) {
    if (!node || !searchText) return node;

    const lower = searchText.toLowerCase();
    const name = (node.name || '').toLowerCase();
    const productRef =
        node.productRef !== undefined && node.productRef !== null
            ? String(node.productRef).toLowerCase()
            : '';
    const selfMatches = name.includes(lower) || productRef.includes(lower);

    let filteredChildren = [];
    if (Array.isArray(node.children)) {
        filteredChildren = node.children
            .map((child) => filterTreeBySearch(child, searchText))
            .filter(Boolean);
    }

    if (selfMatches || filteredChildren.length > 0) {
        return {
            ...node,
            children: filteredChildren,
        };
    }

    return null;
}

function getNodeQuantity(node) {
    if (!node) return null;
    return (
        node.quantity ??
        node.qty ??
        node.Qty ??
        node.childCount ??
        null
    );
}

function renderAssemblyWithQuantities(node, level = 0) {
    if (!node) return null;
    const qty = getNodeQuantity(node);

    return (
        <Box key={node._id || `${node.name}-${level}-${Math.random()}`}>
            <Typography
                variant="body2"
                sx={{
                    fontSize: 12,
                    pl: level * 2,
                    mb: 0.25,
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                }}
            >
                {node.name || '(no name)'}
                {qty != null && `  x${qty}`}
            </Typography>
            {Array.isArray(node.children) &&
                node.children.map((child) =>
                    renderAssemblyWithQuantities(child, level + 1)
                )}
        </Box>
    );
}

const HEX_COLOR_REGEX = /^#?[0-9a-fA-F]{6}$/;
const normalizeHexColor = (value) => {
    const trimmed = String(value || "").trim();
    if (!HEX_COLOR_REGEX.test(trimmed)) return null;
    return trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
};

// ------------------------- main component -------------------------

const ModelViewer = () => {
    const { machine_id, fileName } = useParams();
    const { selectedSiteData, selectedSite, getSitePrice } = useSite();
    const { user } = useAuth();
    const theme = useTheme();
    const { t } = useTranslation();

    const navigate = useNavigate()
    const location = useLocation();
    const returnPath = useMemo(() => {
        const fromPath = location?.state?.fromPath;
        if (typeof fromPath === "string" && fromPath.trim()) return fromPath;
        return "/machines";
    }, [location?.state?.fromPath]);
    // machine + products
    const [machine, setMachine] = useState(null);
    const [error, setError] = useState(null);
    const [products, setProducts] = useState([]);
    const [machineName, setMachineName] = useState('');
    const [machineNumber, setMachineNumber] = useState('');
    const [bomTreeData, setBomTreeData] = useState([]);

    // left panel / tabs
    const [showMenuBox, setShowMenuBox] = useState(true);
    const [tabValue, setTabValue] = useState(0);

    // dialogs
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [bomOpen, setBomOpen] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    const [ccrOpen, setCcrOpen] = useState(false);
    const [ccrPrefill] = useState(null);

    // part menu
    const [showPartMenu, setShowPartMenu] = useState(false);
    const [partMenuData, setPartMenuData] = useState(null);

    // multiple parts menu
    const [showMultiPartMenu, setShowMultiPartMenu] = useState(false);
    const [multiPartSelection, setMultiPartSelection] = useState([]);

    // loading / progress
    const [loading, setLoading] = useState(true);
    const [modelLoaded, setModelLoaded] = useState(false);
    const [dataReady, setDataReady] = useState(false);
    const [percentage, setPercentage] = useState(0);
    const [loadingStep, setLoadingStep] = useState(0);
    const [cartCount] = useState(0);
    const [cartOpen, setCartOpen] = useState(false);

    // part menu drag
    const [partMenuDelta, setPartMenuDelta] = useState({ x: 0, y: 0 });
    const [isDraggingPartMenu, setIsDraggingPartMenu] = useState(false);
    const [multiPartMenuDelta, setMultiPartMenuDelta] = useState({ x: 0, y: 0 });
    const [isDraggingMultiPartMenu, setIsDraggingMultiPartMenu] = useState(false);
    const dragStartRef = useRef({ mouseX: 0, mouseY: 0, dx: 0, dy: 0 });
    const multiDragStartRef = useRef({ mouseX: 0, mouseY: 0, dx: 0, dy: 0 });

    // three scene
    const sceneRef = useRef(null);
    const lastFetchKeyRef = useRef(null);

    // assembly / tree
    const [selectedPart, setSelectedPart] = useState(null);
    const [selectedParts, setSelectedParts] = useState([]); // all current logical selections
    const [assemblyTree, setAssemblyTree] = useState(null);
    const assemblyIndex = useMemo(
        () => (assemblyTree ? buildAssemblyIndex(assemblyTree) : null),
        [assemblyTree]
    );

    const [selectedNodeId, setSelectedNodeId] = useState(null);
    const [expanded, setExpanded] = useState([]);
    const [treeSearchTerm, setTreeSearchTerm] = useState('');
    const [treeSearchMatches, setTreeSearchMatches] = useState([]);
    const [treeSearchIndex, setTreeSearchIndex] = useState(0);

    const [searchText, setSearchText] = useState('');
    const [nodeVisibility, setNodeVisibility] = useState({});
    const treeItemRefs = useRef(new Map());
    const partsTreeScrollRef = useRef(null);
    const subAssembliesScrollRef = useRef(null);
    const rsplScrollRef = useRef(null);

    const [subSearchTerm, setSubSearchTerm] = useState('');
    const [selectedSubAssemblyIds, setSelectedSubAssemblyIds] = useState([]);

    // nav mode for orbit controls
    const [navMode, setNavMode] = useState('rotate'); // 'rotate' | 'pan'

    // explode state for slider
    const [explodeEnabled, setExplodeEnabled] = useState(false);
    const [explodeValue, setExplodeValue] = useState(0.5); // 0..1

    const [occurrenceToggle, setOccurrenceToggle] = useState(false);
    const [machineTransparentToggle, setMachineTransparentToggle] = useState(false);
    const [partTransparencyToggle, setPartTransparencyToggle] = useState(false);
    const [isolateSelectionToggle, setIsolateSelectionToggle] = useState(false);
    const [selectVisibleActive, setSelectVisibleActive] = useState(false);
    const [modelVisible, setModelVisible] = useState(true);
    const [non3DMessageVisible, setNon3DMessageVisible] = useState(false);
    const [non3DPartNumber, setNon3DPartNumber] = useState("");

    const [progressPhase, setProgressPhase] = useState("loading"); // "loading" | "finishing"
    // Selected inner tab (product type)

    const [rsplSearchTerm, setRsplSearchTerm] = useState("");
    const [rsplSearchResults, setRsplSearchResults] = useState([]);
    const [rsplSearchIndex, setRsplSearchIndex] = useState(0);
    const [activeRsplPartNumber, setActiveRsplPartNumber] = useState(null);

    const [rsplReady, setRsplReady] = useState(false);
    const [rsplLoading, setRsplLoading] = useState(false);
    const keepPartMenuOnNextClearRef = useRef(false);
    const skipMatrixApplyOnNextDeselectRef = useRef(false);
    const hiddenPartsFiloStackRef = useRef([]);
    const { optionKeys } = useOptions();
    const modelOrientation = useMemo(() => resolveModelOrientation(optionKeys || {}), [optionKeys]);
    const machineViewMode = useMemo(() => {
        if (isolateSelectionToggle && machineTransparentToggle) {
            return "isolatedMachineTransparent";
        }
        if (isolateSelectionToggle) {
            return "isolated";
        }
        if (machineTransparentToggle) {
            return "machineTransparent";
        }
        return "normal";
    }, [isolateSelectionToggle, machineTransparentToggle]);
    const defaultUpAxis = `${modelOrientation.up}_UP`;
    const defaultCameraCorner = useMemo(
        () => normalizeCameraCorner(optionKeys?.modelViewerDefaultCameraCorner),
        [optionKeys?.modelViewerDefaultCameraCorner]
    );
    const defaultLightIntensity = useMemo(() => {
        const parsed = Number.parseFloat(optionKeys?.modelViewerLightIntensity);
        return Number.isFinite(parsed) ? parsed : 1;
    }, [optionKeys?.modelViewerLightIntensity]);
    const defaultHighlightColor = useMemo(() => {
        const raw = optionKeys?.modelViewerHighlightColor;
        if (themeColors[raw]) return themeColors[raw].main;
        const fromOptions = normalizeHexColor(raw);
        const fromTheme = normalizeHexColor(theme?.palette?.primary?.light);
        return fromOptions || fromTheme || "#ffcc00";
    }, [optionKeys?.modelViewerHighlightColor, theme?.palette?.primary?.light]);
    const defaultHighlightOpacity = useMemo(() => {
        const parsed = Number.parseFloat(optionKeys?.modelViewerHighlightOpacity);
        return Number.isFinite(parsed) ? Math.min(1, Math.max(0, parsed)) : 1;
    }, [optionKeys?.modelViewerHighlightOpacity]);
    const showMenuTabSubtitles = optionKeys?.modelViewerShowTabSubtitles !== "false";
    const menuTabLabels = useMemo(() => ({
        subAssemblies: optionKeys?.modelViewerTabTitleSubAssemblies || "Sub Assemblies",
        rspl: optionKeys?.modelViewerTabTitleRspl || "RSPL Parts",
        partsTree: optionKeys?.modelViewerTabTitlePartsTree || "BOM",
    }), [
        optionKeys?.modelViewerTabTitleSubAssemblies,
        optionKeys?.modelViewerTabTitleRspl,
        optionKeys?.modelViewerTabTitlePartsTree,
    ]);
    const gizmoAxisLabels = useMemo(() => {
        return ["X", "Y", "Z"];
    }, []);


    const [bomData, setBomData] = useState([]);
    const [settings, setSettings] = useState({
        navMode: "rotate",
        transparentOnSelect: false,
        explodeEnabled: false,
        explodeValue: 0.5,
        autoZoom: false,
        showGizmo: true,
        light: defaultLightIntensity,
    });

    const handleHelpClick = () => {
        setHelpOpen(true);
    };

    useEffect(() => {
        setNavMode(settings.navMode);
    }, [settings.navMode]);

    useEffect(() => {
        setExplodeEnabled(settings.explodeEnabled);
    }, [settings.explodeEnabled]);

    useEffect(() => {
        setExplodeValue(settings.explodeValue);
    }, [settings.explodeValue]);

    useEffect(() => {
        setSettings((prev) => ({ ...prev, light: defaultLightIntensity }));
    }, [defaultLightIntensity]);

    // ---- CART + REQUEST LIST STATE ----
    const [cartItems, setCartItems] = useState([]);
    const [requestItems, setRequestItems] = useState([]);
    const [orderingItems, setOrderingItems] = useState(false);

    const normalizePartMeta = (part = {}) => {
        const fallbackNumber = part.part_number || part.partNumber || part.product_number || null;
        let normalizedNumber = fallbackNumber ? String(fallbackNumber) : null;
        let normalizedProductId = part.product_id || part.productId || part.ProductID || null;
        let normalizedPrice = part.price;
        let normalizedPurchasable =
            part.purchasable !== undefined && part.purchasable !== null
                ? part.purchasable
                : null;

        if (normalizedNumber && (!normalizedProductId || normalizedPrice == null || normalizedPurchasable == null)) {
            const match = products?.find(
                (p) => p.product_number?.toString().trim() === normalizedNumber?.toString().trim()
            );
            if (match) {
                if (!normalizedProductId) normalizedProductId = match.product_id;
                if (normalizedPrice == null) normalizedPrice = getSitePrice(match);
                if (normalizedPurchasable == null) normalizedPurchasable = match.purchasable;
            }
        }

        return {
            ...part,
            part_number: normalizedNumber,
            product_id: normalizedProductId,
            price: normalizedPrice,
            purchasable: normalizedPurchasable,
        };
    };

    // Add to Cart
    const addToCart = (part) => {
        const normalized = normalizePartMeta(part);
        if (!normalized.part_number) return;

        setCartItems((prev) => {
            const exists = prev.find((item) => item.part_number === normalized.part_number);
            if (exists) return prev; // prevent duplicates

            return [
                ...prev,
                {
                    ...normalized,
                    qty: normalized.qty ?? 1,
                },
            ];
        });
    };

    // Add to Request List
    const addToRequestList = (part) => {
        const normalized = normalizePartMeta(part);
        if (!normalized.part_number) return;

        setRequestItems((prev) => {
            const exists = prev.find((item) => item.part_number === normalized.part_number);
            if (exists) return prev;

            return [...prev, normalized];
        });
    };

    // Remove item
    const removeCartItem = (part_number) =>
        setCartItems(prev => prev.filter(item => item.part_number !== part_number));

    const removeRequestItem = (part_number) =>
        setRequestItems(prev => prev.filter(item => item.part_number !== part_number));


    // Quantity handlers
    const incrementQty = (part_number) =>
        setCartItems(prev =>
            prev.map(item =>
                item.part_number === part_number
                    ? { ...item, qty: item.qty + 1 }
                    : item
            )
        );

    const decrementQty = (part_number) =>
        setCartItems(prev =>
            prev.map(item =>
                item.part_number === part_number && item.qty > 1
                    ? { ...item, qty: item.qty - 1 }
                    : item
            )
        );

    // Check if part is in cart or request list
    const isInCart = (pn) => cartItems.some(i => i.part_number === pn);
    const isInRequest = (pn) => requestItems.some(i => i.part_number === pn);



    const normalizeRsplType = (value) => String(value || "").trim().toLowerCase();
    const normalizeRsplCode = (value) => String(value || "").trim().toUpperCase();
    const normalizeRsplList = (value) => {
        if (!value) return null;
        const parsed = Array.isArray(value)
            ? value
            : (() => {
                try {
                    return JSON.parse(value);
                } catch {
                    return null;
                }
            })();
        if (!Array.isArray(parsed)) return null;
        const sanitized = parsed.filter(
            (item) =>
                item &&
                typeof item.value === "string" &&
                Array.isArray(item.key) &&
                item.key.every((entry) => typeof entry === "string")
        );
        return sanitized.length > 0 ? sanitized : null;
    };

    const resolvedRsplList = useMemo(() => {
        return normalizeRsplList(optionKeys?.rsplList) || RSPL_LIST || [];
    }, [optionKeys?.rsplList]);

    const rsplTypeLabelMap = useMemo(() => {
        const map = new Map();
        (resolvedRsplList || []).forEach(({ value }) => {
            if (!value || typeof value !== "string") return;
            const key = normalizeRsplType(value);
            if (!map.has(key)) map.set(key, value);
        });
        return map;
    }, [resolvedRsplList]);

    const rsplCodeToTypeKey = useMemo(
        () =>
            Object.fromEntries(
                (resolvedRsplList || []).flatMap(({ key, value }) =>
                    key.map((k) => [normalizeRsplCode(k), normalizeRsplType(value)])
                )
            ),
        [resolvedRsplList]
    );

    const applyRsplTypeMetadata = React.useCallback(
        (machineProducts = []) => {
            return (machineProducts || []).map((product) => {
                const normalizedRsplCode = normalizeRsplCode(product?.product_rspl_code);
                const productTypeKey = rsplCodeToTypeKey[normalizedRsplCode] || null;
                const productTypeLabel = productTypeKey
                    ? (rsplTypeLabelMap.get(productTypeKey) || productTypeKey)
                    : t('UI_TEXT.OTHER');
                const imageUrl = resolveProductCardImageUrl(product);

                return {
                    ...product,
                    ProductType: productTypeLabel,
                    ProductTypeKey: productTypeKey,
                    productImageURL: imageUrl,
                };
            });
        },
        [rsplCodeToTypeKey, rsplTypeLabelMap, t]
    );

    // -------- part menu drag handlers --------
    const handlePartMenuMouseDown = (e) => {
        if (e.button !== 0) return; // left-click only
        setIsDraggingPartMenu(true);
        dragStartRef.current = {
            mouseX: e.clientX,
            mouseY: e.clientY,
            dx: partMenuDelta.x,
            dy: partMenuDelta.y,
        };
    };

    const handleMultiPartMenuMouseDown = (e) => {
        if (e.button !== 0) return;
        setIsDraggingMultiPartMenu(true);
        multiDragStartRef.current = {
            mouseX: e.clientX,
            mouseY: e.clientY,
            dx: multiPartMenuDelta.x,
            dy: multiPartMenuDelta.y,
        };
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDraggingPartMenu) {
                const { mouseX, mouseY, dx, dy } = dragStartRef.current;
                const newDx = dx + (e.clientX - mouseX);
                const newDy = dy + (e.clientY - mouseY);

                setPartMenuDelta({
                    x: newDx,
                    y: newDy,
                });
            }

            if (isDraggingMultiPartMenu) {
                const { mouseX, mouseY, dx, dy } = multiDragStartRef.current;
                const newDx = dx + (e.clientX - mouseX);
                const newDy = dy + (e.clientY - mouseY);

                setMultiPartMenuDelta({
                    x: newDx,
                    y: newDy,
                });
            }
        };

        const handleMouseUp = () => {
            if (isDraggingPartMenu) {
                setIsDraggingPartMenu(false);
            }
            if (isDraggingMultiPartMenu) {
                setIsDraggingMultiPartMenu(false);
            }
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDraggingPartMenu, isDraggingMultiPartMenu, partMenuDelta.x, partMenuDelta.y, multiPartMenuDelta.x, multiPartMenuDelta.y]);

    // --------- data fetching ---------

    useEffect(() => {
        if (!isEmpty(machine_id) && selectedSiteData?.site_id && selectedSite) {
            const key = `${machine_id}-${selectedSiteData.site_id}-${selectedSite}`;

            // Prevent repeated fetches for the same machine/site combo
            if (lastFetchKeyRef.current === key) return;

            lastFetchKeyRef.current = key;
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [machine_id, selectedSiteData?.site_id, selectedSite]);

    const fetchData = async () => {
        if (isFatalError) return; // 🚫 DO NOT START LOADING AGAIN

        setLoading(true);
        setDataReady(false);
        setError(null);
        setPercentage(0);
        setLoadingStep(0);
        setModelLoaded(false);


        const productsReqBody = {
            machine_id: machine_id,
            site_number: selectedSite,
        };

        try {
            const [machineResponse, productsResponse] = await Promise.all([
                getMachineByNumberOrId({
                    machine_id,
                    site_id: selectedSiteData?.site_id,
                }),
                getProductsByMachineId(productsReqBody),
            ]);

            let hadError = false;

            // machine
            if (machineResponse?.status && machineResponse?.data?.length) {
                const machineData = machineResponse.data[0];
                setMachine(machineData);
                setMachineName(machineData.machine_name);
                setMachineNumber(machineData.machine_number);
            } else {
                hadError = true;
                setMachine(null);
                setError('Failed fetching machine details.');
            }

            // products
            if (productsResponse?.status) {
                const { products: machineProducts, bomTree } =
                    normalizeMachineProductsResponse(productsResponse);

                const productsWithType = applyRsplTypeMetadata(machineProducts);


                setBomTreeData(bomTree || []);
                setProducts(productsWithType);
            } else {
                hadError = true;
                setBomTreeData([]);
                setError((prev) => prev || 'Failed to fetch products.');
            }

            if (hadError) {
                setLoading(false);
            } else {
                setDataReady(true);
            }
        } catch (err) {
            console.error('Error fetching machine or product details', err);
            setMachine(null);
            setBomTreeData([]);
            setProducts([]);
            setError('Failed fetching machine/products details.');
            setLoading(false);
        }
    };

    useEffect(() => {
        setProducts((prev) => {
            if (!Array.isArray(prev) || prev.length === 0) return prev;
            return applyRsplTypeMetadata(prev);
        });
    }, [applyRsplTypeMetadata]);

    const productTypes = useMemo(() => {
        const labels = (resolvedRsplList || [])
            .map((item) => String(item?.value || "").trim())
            .filter(Boolean);
        return Array.from(new Set(labels));
    }, [resolvedRsplList]);


    const productsByType = useMemo(() => {
        const map = {};
        productTypes.forEach((label) => {
            const key = normalizeRsplType(label);
            if (key) map[key] = [];
        });

        products.forEach((p) => {
            const key = p.ProductTypeKey;
            if (key && map[key]) {
                map[key].push(p);
            }
        });

        return map;
    }, [products, productTypes]);


    const [innerTab, setInnerTab] = useState(t('MENU_ITEMS.ALL'));

    useEffect(() => {
        if (productTypes.length > 0) {
            setInnerTab(t('MENU_ITEMS.ALL'));
        } else {
            setInnerTab("");
        }
    }, [productTypes]);



    const isFatalError = error === "Model Not Found";

    const normalizePartToken = (value) =>
        String(value ?? "")
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]/g, "");

    const getProductRefsForProduct = (productOrNumber) => {
        if (!assemblyIndex || !assemblyIndex.nodeById) return [];

        const product =
            typeof productOrNumber === "object" && productOrNumber !== null
                ? productOrNumber
                : { product_number: productOrNumber };

        const refs = new Set();
        const partNumberRaw = String(product?.product_number || "").trim();
        const partNameRaw = String(product?.product_name || "").trim();
        const productIdRaw =
            product?.product_id !== undefined && product?.product_id !== null
                ? String(product.product_id).trim()
                : "";

        const variantSet = new Set(
            [partNumberRaw, partNameRaw]
                .filter(Boolean)
                .flatMap((value) => [value, value.toLowerCase(), normalizePartToken(value)])
                .filter(Boolean)
        );

        assemblyIndex.nodeById.forEach((node) => {
            if (!node) return;
            if (node.productRef === null || node.productRef === undefined) return;

            const hasGeometry = Array.isArray(node.objects) && node.objects.length > 0;
            if (!hasGeometry) return;

            const nodeNameRaw = String(node.name || "").trim();
            const nodeNameLower = nodeNameRaw.toLowerCase();
            const nodeNameNormalized = normalizePartToken(nodeNameRaw);

            const matchesByName = Array.from(variantSet).some((candidate) => {
                const c = String(candidate || "");
                if (!c) return false;
                const lower = c.toLowerCase();
                return (
                    nodeNameLower === lower ||
                    nodeNameLower.startsWith(lower) ||
                    nodeNameNormalized === c ||
                    nodeNameNormalized.startsWith(c)
                );
            });

            if (matchesByName) {
                refs.add(String(node.productRef));
            }
        });

        if (refs.size === 0 && assemblyIndex.productRefToNodeIds) {
            const fallbackKeys = new Set(
                [partNumberRaw, productIdRaw]
                    .filter(Boolean)
                    .flatMap((value) => [value, value.toLowerCase(), normalizePartToken(value)])
            );

            fallbackKeys.forEach((key) => {
                const nodeIds = assemblyIndex.productRefToNodeIds.get(String(key));
                if (!nodeIds || nodeIds.length === 0) return;
                nodeIds.forEach((id) => {
                    const node = assemblyIndex.nodeById.get(id);
                    const hasGeometry = Array.isArray(node?.objects) && node.objects.length > 0;
                    if (!hasGeometry) return;
                    if (node.productRef !== null && node.productRef !== undefined) {
                        refs.add(String(node.productRef));
                    }
                });
            });
        }

        const preferredRaw = String(partNumberRaw || "").toLowerCase();
        const preferredId = String(productIdRaw || "").toLowerCase();
        return Array.from(refs).sort((a, b) => {
            const aKey = String(a).toLowerCase();
            const bKey = String(b).toLowerCase();
            const aPreferred = aKey === preferredRaw || aKey === preferredId;
            const bPreferred = bKey === preferredRaw || bKey === preferredId;
            if (aPreferred && !bPreferred) return -1;
            if (!aPreferred && bPreferred) return 1;
            return aKey.localeCompare(bKey);
        });
    };

    const getSelectableProductRefsForProduct = useCallback((productOrNumber) => {
        const refs = getProductRefsForProduct(productOrNumber);
        if (!modelLoaded || !sceneRef.current?.canSelectAssemblyNode) return refs;
        return refs.filter((ref) =>
            sceneRef.current?.canSelectAssemblyNode?.(String(ref))
        );
    }, [modelLoaded, assemblyIndex]);

    const resolvePrimaryAssemblyNodeForProductRef = useCallback((productRef) => {
        const refKey = String(productRef ?? '').trim();
        if (!assemblyIndex || !refKey) return null;
        const nodeIds = assemblyIndex.productRefToNodeIds?.get(refKey) || [];
        for (const nodeId of nodeIds) {
            const node = assemblyIndex.nodeById?.get(nodeId);
            if (node && Array.isArray(node.objects) && node.objects.length > 0) {
                return node;
            }
        }
        return null;
    }, [assemblyIndex]);

    const getOccurrenceCountForProductRef = useCallback((productRef) => {
        const refKey = String(productRef ?? '').trim();
        if (!assemblyIndex || !refKey) return 0;
        const nodeIds = assemblyIndex.productRefToNodeIds?.get(refKey) || [];
        const uniqueObjectUuids = new Set();
        nodeIds.forEach((nodeId) => {
            const node = assemblyIndex.nodeById?.get(nodeId);
            if (!node || !Array.isArray(node.objects)) return;
            node.objects.forEach((obj) => {
                if (obj?.uuid) uniqueObjectUuids.add(obj.uuid);
            });
        });
        return uniqueObjectUuids.size;
    }, [assemblyIndex]);

    const product3DMap = useMemo(() => {
        const map = {};
        if (!products || !assemblyIndex) return map;

        products.forEach((p) => {
            map[p.product_number] =
                getSelectableProductRefsForProduct(p).length > 0;
        });

        return map;
    }, [products, assemblyIndex, getSelectableProductRefsForProduct]);


    const handleRSPLPartClick = (product) => {
        setSelectedPart(null);
        clearSelectionActionToggles();
        setSelectedParts(null);
        setActiveRsplPartNumber(product.product_number);
        setSelectVisibleActive(false);

        const selectedRefs = getSelectableProductRefsForProduct(product);
        const in3D = selectedRefs.length > 0;

        setShowPartMenu(true);
        setShowMultiPartMenu(false);
        setPartMenuData({
            part_name: product.product_name,
            part_number: product.product_number,
            part_type: product.ProductType,
            product_id: product.product_id,
            price: getSitePrice(product),
            purchasable: product.purchasable,
            isOrderable: true,
            availableIn3D: in3D,
        });

        if (!in3D) {
            // Part exists in RSPL, but no 3D geometry in the assembly
            setNon3DPartNumber(product.product_number || "");
            keepPartMenuOnNextClearRef.current = true;
            sceneRef.current?.showOnlyProductRefs?.([], {
                emptyMode: "hideAll",
                focus: false,
            });
            sceneRef.current?.clearSelection?.();
            setModelVisible(false);
            setNon3DMessageVisible(true);
            return;
        }

        setModelVisible(true);
        setNon3DMessageVisible(false);
        setNon3DPartNumber("");
        keepPartMenuOnNextClearRef.current = false;

        if (!assemblyIndex || !sceneRef.current?.selectAssemblyNode) return;
        const refs = selectedRefs.map((ref) => String(ref));
        const primaryRef = refs[0] || null;
        const primaryNode = resolvePrimaryAssemblyNodeForProductRef(primaryRef);
        const occurrenceCount = getOccurrenceCountForProductRef(primaryRef);
        const hasMultipleOccurrences = occurrenceCount > 1;

        setPartMenuData({
            part_name: product.product_name,
            part_number: product.product_number,
            part_type: product.ProductType,
            product_id: product.product_id,
            price: getSitePrice(product),
            purchasable: product.purchasable,
            isOrderable: true,
            availableIn3D: in3D,
            hasMultipleOccurrences,
            occurrenceCount: occurrenceCount || 1,
        });

        const didSelect = sceneRef.current?.selectAssemblySubtreeByProductRefs?.(refs, {
            primaryProductRef: primaryRef || refs[0],
            focus: false,
            emitSelectionCallbacks: false,
            includeAllOccurrences: false,
        });

        if (didSelect) {
            setSelectedPart({
                name: product.product_name || primaryNode?.name || product.product_number || "Selected Part",
                uuid: `rspl-${product.product_number || primaryRef || Date.now()}`,
                type: "RSPLPart",
                userData: {},
                material: null,
                object3D: null,
                assemblyNode: primaryNode,
            });
            setSelectedParts([]);
        }

        if (!didSelect) {
            // Last-resort fallback: keep menu data, hide model content, and show non-3D message.
            setNon3DPartNumber(product.product_number || "");
            keepPartMenuOnNextClearRef.current = true;
            sceneRef.current?.showOnlyProductRefs?.([], {
                emptyMode: "hideAll",
                focus: false,
            });
            sceneRef.current?.clearSelection?.();
            setModelVisible(false);
            setNon3DMessageVisible(true);
            return;
        }

        setIsolateSelectionToggle(true);
    };

    const currentRsplProductRefs = useMemo(() => {
        if (!assemblyIndex || !rsplReady) return [];

        let targetProducts = [];
        if (innerTab === t('MENU_ITEMS.ALL')) {
            productTypes.forEach((type) => {
                const key = normalizeRsplType(type);
                const items = productsByType[key] ?? [];
                targetProducts.push(...items);
            });
        } else {
            const key = normalizeRsplType(innerTab);
            const items = productsByType[key] ?? [];
            targetProducts.push(...items);
        }

        const refSet = new Set();
        targetProducts.forEach((p) => {
            if (!product3DMap[p.product_number]) return;
            const refs = getProductRefsForProduct(p);
            refs.forEach((r) => refSet.add(r));
        });

        return Array.from(refSet);
    }, [
        assemblyIndex,
        rsplReady,
        innerTab,
        productTypes,
        productsByType,
        product3DMap,
    ]);

    useEffect(() => {
        // Need model + assembly + RSPL parts + the helper from ModelScene
        if (!sceneRef.current || !assemblyIndex) return;
        if (machineViewMode !== "normal") return;

        // If not on RSPL tab or RSPL not ready -> show everything again
        if (tabValue !== 1 || !rsplReady) {
            sceneRef.current.showOnlyProductRefs?.([]);
            return;
        }

        sceneRef.current.showOnlyProductRefs?.(currentRsplProductRefs, {
            emptyMode: "hideAll",
            focus: false,
        });
    }, [
        tabValue,
        rsplReady,
        assemblyIndex,
        currentRsplProductRefs,
        machineViewMode,
    ]);

    useEffect(() => {
        if (tabValue !== 1 || !rsplReady) return;
        if (!selectedPart) return;
        if (machineViewMode !== "normal") return;

        const selectedName = selectedPart?.assemblyNode?.name || selectedPart?.name || "";
        const matched = findMatchingProductForPart(
            selectedName,
            selectedPart?.assemblyNode?.productRef
        );
        const selectedRefs = new Set();
        const nodeProductRef = selectedPart?.assemblyNode?.productRef;
        if (nodeProductRef !== undefined && nodeProductRef !== null) {
            selectedRefs.add(String(nodeProductRef));
        }
        if (matched) {
            const matchedRefs = getProductRefsForProduct(matched);
            matchedRefs.forEach((ref) => selectedRefs.add(String(ref)));
        }
        if (selectedRefs.size === 0) return;

        const refSet = new Set(currentRsplProductRefs);
        const isVisibleInFilter = Array.from(selectedRefs).some((r) => refSet.has(r));

        if (!isVisibleInFilter) {
            sceneRef.current?.clearSelection?.();
            setSelectedPart(null);
            clearSelectionActionToggles();
            setSelectedParts(null);
            setShowPartMenu(false);
            setShowMultiPartMenu(false);
        }
    }, [
        tabValue,
        rsplReady,
        selectedPart,
        currentRsplProductRefs,
        machineViewMode,
    ]);




    useEffect(() => {
        if (cartItems.length > 0 || requestItems.length > 0) {
            setCartOpen(true);
        }
    }, [cartItems, requestItems]);



    useEffect(() => {
        if (!rsplSearchTerm) {
            setRsplSearchResults([]);
            setRsplSearchIndex(0);
            return;
        }

        const lower = rsplSearchTerm.toLowerCase();
        const matches = products.filter(
            (p) =>
                p.ProductTypeKey &&
                (p.product_name.toLowerCase().includes(lower) ||
                    p.product_number.toLowerCase().includes(lower))
        );

        setRsplSearchResults(matches);
        setRsplSearchIndex(0);   // reset index

        if (matches.length > 0) {
            const match = matches[0];

            const typeToOpen =
                match.ProductTypeKey ? (match.ProductType || t('MENU_ITEMS.ALL')) : t('MENU_ITEMS.ALL');
            setInnerTab(typeToOpen);

            setTimeout(() => {
                const el = document.getElementById(`rspl-${match.product_number}`);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 150);
        }
    }, [rsplSearchTerm]);


    const handleRsplSearchSubmit = () => {
        if (rsplSearchResults.length === 0) return;

        const nextIndex = (rsplSearchIndex + 1) % rsplSearchResults.length;
        setRsplSearchIndex(nextIndex);

        const match = rsplSearchResults[nextIndex];

        // Switch tab
        const typeToOpen = match.ProductTypeKey ? (match.ProductType || t('MENU_ITEMS.ALL')) : t('MENU_ITEMS.ALL');
        setInnerTab(typeToOpen);

        // Scroll to card
        setTimeout(() => {
            const el = document.getElementById(`rspl-${match.product_number}`);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 150);
    };




    // ---------- loading progress ----------

    const allReady = dataReady && modelLoaded;

    const [modelFilesExist, setModelFilesExist] = useState(null); // null = unknown, true/false later
    const [loadingStopped, setLoadingStopped] = useState(false);

    const checkModelFilesExist = async () => {
        const glbURL = `${SERVER_BASE_URL}/stock-models/GLB/Models/${fileName}.glb`;
        const bomURL = `${SERVER_BASE_URL}/stock-models/GLB/Boms/${fileName}.json`;

        try {
            const [glbRes, bomRes] = await Promise.all([
                fetch(glbURL, { method: "HEAD" }),
                fetch(bomURL, { method: "HEAD" }),
            ]);

            const filesOk = glbRes.ok && bomRes.ok;
            setModelFilesExist(filesOk);
            return filesOk;
        } catch {
            setModelFilesExist(false);
            return false;
        }
    };



    useEffect(() => {
        if (!fileName) return;

        const verify = async () => {
            if (isFatalError) return;  // stop entirely

            setLoading(true);
            setLoadingStopped(false);


            const exists = await checkModelFilesExist();

            if (!exists) {
                // STOP ALL LOADING
                setError("Model Not Found");
                setLoadingStopped(true);
                setLoading(false);
                setPercentage(0);
            }
        };

        verify();
    }, [fileName]);

    useEffect(() => {
        if (!loading || loadingStopped || isFatalError) return;

        const interval = setInterval(() => {
            setPercentage((prev) => {
                if (prev >= 100) return 100;

                if (progressPhase === "loading") {
                    if (prev < 25) return prev + 8;
                    if (prev < 60) return prev + 4;
                    if (prev < 85) return prev + 2;
                    return Math.min(prev + 1, 96);
                }

                if (progressPhase === "finishing") {
                    if (prev < 96) return prev + 6;
                    return Math.min(prev + 12, 100);
                }

                return prev;
            });
        }, 110);

        return () => clearInterval(interval);
    }, [loading, loadingStopped, progressPhase]);



    useEffect(() => {
        if (isFatalError) return;
        if (allReady && percentage >= 100) {
            setLoading(false);
        }

    }, [allReady, percentage]);

    useEffect(() => {
        if (percentage < 25) {
            setLoadingStep(0);
        } else if (percentage < 60) {
            setLoadingStep(1);
        } else if (percentage < 90) {
            setLoadingStep(2);
        } else {
            setLoadingStep(3);
        }
    }, [percentage]);

    useEffect(() => {
        if (dataReady && modelLoaded) {
            setProgressPhase("finishing");
        }
    }, [dataReady, modelLoaded]);

    // ---------- assembly / tree wiring ----------

    useEffect(() => {
        if (!assemblyIndex) return;

        const { nodeById, rootId } = assemblyIndex;
        const vis = {};
        nodeById.forEach((_node, id) => {
            vis[id] = true;
        });
        setNodeVisibility(vis);
        setExpanded([rootId]);
    }, [assemblyIndex]);

    const level1SubAssemblies = useMemo(() => {
        if (!assemblyIndex) return [];
        const { nodeById, parentById, rootId } = assemblyIndex;
        const result = [];
        nodeById.forEach((node, id) => {
            const hasChildren =
                Array.isArray(node?.children) && node.children.length > 0;
            if (parentById.get(id) === rootId && hasChildren) {
                result.push({ id, node });
            }
        });
        return result;
    }, [assemblyIndex]);

    const filteredSubAssemblies = useMemo(() => {
        if (!subSearchTerm) return level1SubAssemblies;
        const lower = subSearchTerm.trim().toLowerCase();
        return (level1SubAssemblies || []).filter(({ node }) => {
            const nodeName = (node?.name || "").trim().toLowerCase();
            const productRef =
                node?.productRef !== undefined && node?.productRef !== null
                    ? String(node.productRef).trim().toLowerCase()
                    : "";

            const matchedProduct =
                products.find((p) => {
                    const num = p.product_number?.trim().toLowerCase();
                    const pname = p.product_name?.trim().toLowerCase();
                    const pid =
                        p.product_id !== undefined && p.product_id !== null
                            ? String(p.product_id).trim().toLowerCase()
                            : "";

                    return (
                        (productRef && productRef === num) ||
                        (productRef && productRef === pid) ||
                        nodeName === num ||
                        nodeName.startsWith(num || "") ||
                        (num && nodeName.includes(num)) ||
                        nodeName === pname ||
                        (pname && nodeName.startsWith(pname)) ||
                        (pname && nodeName.includes(pname))
                    );
                }) || null;

            const haystacks = [
                node?.name,
                node?.productName,
                node?.productNumber,
                node?.product_name,
                node?.product_number,
                node?.productRef,
                matchedProduct?.product_name,
                matchedProduct?.product_number,
            ]
                .filter(Boolean)
                .map((value) => String(value).toLowerCase());

            return haystacks.some((value) => value.includes(lower));
        });
    }, [level1SubAssemblies, subSearchTerm, products]);

    const handleOccurrenceToggle = () => {
        if (!selectedPart) return;

        if (!occurrenceToggle) {
            // Expand selection to all occurrences
            sceneRef.current?.selectAllOccurrences?.();
        } else {
            // Revert to single occurrence
            sceneRef.current?.selectSingleOccurrence?.(selectedPart.uuid);
        }
        setOccurrenceToggle(!occurrenceToggle);
    };

    const clearSelectionActionToggles = () => {
        setPartTransparencyToggle(false);
        setIsolateSelectionToggle(false);
    };

    const findMatchingProductForPart = (assemblyNodeName, productRef = null) => {
        if (!products?.length) return null;

        const name = (assemblyNodeName || '').trim().toLowerCase();
        const ref =
            productRef !== undefined && productRef !== null
                ? String(productRef).trim().toLowerCase()
                : '';

        return (
            products.find((p) => {
                const num = p.product_number?.trim().toLowerCase();
                const pname = p.product_name?.trim().toLowerCase();
                const pid = p.product_id !== undefined && p.product_id !== null
                    ? String(p.product_id).trim().toLowerCase()
                    : '';

                return (
                    (ref && ref === num) ||
                    (ref && ref === pid) ||
                    name === num ||
                    name.startsWith(num) ||
                    (num && name.includes(num)) ||
                    name === pname ||
                    (pname && name.startsWith(pname)) ||
                    (pname && name.includes(pname))
                );
            }) || null
        );
    };

    const resolveTreeNodeMeta = (node) => {
        const matchedProduct = findMatchingProductForPart(
            node?.name || '',
            node?.productRef
        );
        const productRefFallback =
            node?.productRef !== undefined && node?.productRef !== null
                ? String(node.productRef)
                : '';
        const partName =
            matchedProduct?.product_name ||
            node?.name ||
            productRefFallback ||
            '(no name)';
        const partNumber = matchedProduct?.product_number || productRefFallback || '';

        return {
            partName,
            partNumber,
            description: node?.description ? String(node.description) : '',
        };
    };

    const expandAllTreeNodes = () => {
        if (!assemblyIndex) return;
        setExpanded(Array.from(assemblyIndex.nodeById.keys()));
    };

    const collapseAllTreeNodes = () => {
        if (!assemblyIndex?.rootId) {
            setExpanded([]);
            return;
        }
        setExpanded([assemblyIndex.rootId]);
    };

    const scrollPartsTreeToTop = () => {
        partsTreeScrollRef.current?.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    };

    const scrollSubAssembliesToTop = () => {
        subAssembliesScrollRef.current?.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    };

    const scrollRsplToTop = () => {
        rsplScrollRef.current?.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    };



    const handlePartSelected = (partMeta) => {
        setOccurrenceToggle(false);

        if (!partMeta) {
            setSelectedPart(null);
            clearSelectionActionToggles();
            setSelectedNodeId(null);
            if (keepPartMenuOnNextClearRef.current) {
                keepPartMenuOnNextClearRef.current = false;
                return;
            }
            setPartMenuData(null);
            setShowPartMenu(false);
            return;
        }

        setSelectedPart(partMeta);

        // hook into tree
        const node = partMeta.assemblyNode;
        if (node && node._id) {
            setSelectedNodeId(node._id);
        }


        const occurrenceCount = Array.isArray(node?.objects)
            ? node.objects.length
            : 1;
        const hasMultipleOccurrences = occurrenceCount > 1;

        // populate part menu data and show
        const assemblyName = node?.name || partMeta.name || "";
        const matchedProduct = findMatchingProductForPart(
            assemblyName,
            node?.productRef
        );
        // console.log(products);
        let isOrderable = !!matchedProduct;

        const resolvedPartType = matchedProduct?.ProductType || matchedProduct?.ProductTypeKey || "N/A";
        let partData = {
            part_name: matchedProduct?.product_name || assemblyName || "Selected Part",
            part_number: isOrderable
                ? matchedProduct.product_number
                : null,
            part_type: isOrderable
                ? resolvedPartType
                : null,
            url: isOrderable ? `/machines/${machine_id}/${matchedProduct.product_id}` : "",
            price: isOrderable ? getSitePrice(matchedProduct) : 0,
            purchasable: isOrderable ? matchedProduct.purchasable : 0,
            product_id: isOrderable ? matchedProduct.product_id : null,
            hasMultipleOccurrences,
            occurrenceCount,
            isOrderable: isOrderable,   // 🚀 ADD THIS FLAG
        };


        // Update PartMenu UI
        setPartMenuData(partData);

        // Show menu
        setShowPartMenu(true);

        if (settings.transparentOnSelect) {
            setPartTransparencyToggle(true);
        }
        if (settings.autoZoom) {
            sceneRef.current?.zoomToSelectionToggle?.();
        }

    };

    useEffect(() => {
        const currentSelection = Array.isArray(selectedParts)
            ? selectedParts.filter(Boolean)
            : [];

        if (currentSelection.length > 1) {
            setMultiPartSelection(currentSelection);
            setShowMultiPartMenu(true);
            setShowPartMenu(false);
            return;
        }

        if (currentSelection.length <= 1) {
            setShowMultiPartMenu(false);
            if (currentSelection.length === 0) setMultiPartSelection([]);
        }
    }, [selectedParts]);

    const handleVisibilityChange = (productVisibility) => {
        if (!assemblyIndex) return;

        const { nodeById, parentById, productRefToNodeIds, rootId } = assemblyIndex;
        const productVis = productVisibility || {};

        // 1) Start with everything "not visible"
        const baseVis = {};
        nodeById.forEach((_node, id) => {
            baseVis[id] = false;
        });

        // 2) Apply visibility coming from productRef -> visible map
        Object.entries(productVis).forEach(([productRefStr, visible]) => {
            const nodeIds = productRefToNodeIds.get(productRefStr);
            if (!nodeIds) return;
            nodeIds.forEach((id) => {
                baseVis[id] = visible;
            });
        });

        // 3) Build a parent -> children map for the assembly tree
        const childrenByParent = new Map();
        parentById.forEach((parentId, id) => {
            if (!childrenByParent.has(parentId)) {
                childrenByParent.set(parentId, []);
            }
            childrenByParent.get(parentId).push(id);
        });

        const computed = { ...baseVis };

        // 4) Recursively compute visibility so parents follow children
        const computeForNode = (id) => {
            const node = nodeById.get(id);
            if (!node) return false;

            const childIds = childrenByParent.get(id);
            let childrenVisible = false;

            if (childIds && childIds.length) {
                childIds.forEach((childId) => {
                    if (computeForNode(childId)) {
                        childrenVisible = true;
                    }
                });
            }

            // Node is visible if it itself is visible OR any child is visible
            const selfVisible = baseVis[id];
            const result = selfVisible || childrenVisible;
            computed[id] = result;
            return result;
        };

        if (rootId) {
            computeForNode(rootId);
        }

        setNodeVisibility(computed);
    };


    // auto-expand ancestors when selectedNodeId changes
    useEffect(() => {
        if (!selectedNodeId || !assemblyIndex) return;

        const { parentById } = assemblyIndex;

        setExpanded((prev) => {
            const toExpand = new Set(prev);
            let curr = selectedNodeId;
            while (parentById.has(curr)) {
                const parentId = parentById.get(curr);
                toExpand.add(parentId);
                curr = parentId;
            }
            return Array.from(toExpand);
        });
    }, [selectedNodeId, assemblyIndex]);

    // scroll to the selected tree item once expanded
    useEffect(() => {
        if (!selectedNodeId) return;

        const el = treeItemRefs.current.get(selectedNodeId);
        if (el) {
            el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
    }, [selectedNodeId, expanded]);

    useEffect(() => {
        if (!assemblyIndex) return;

        // Clear when search is empty
        if (!treeSearchTerm) {
            setTreeSearchMatches([]);
            setTreeSearchIndex(0);
            return;
        }

        const lower = treeSearchTerm.toLowerCase();
        const matches = [];

        assemblyIndex.nodeById.forEach((node, id) => {
            const { partName, partNumber, description } = resolveTreeNodeMeta(node);
            const searchFields = [partName, partNumber, description]
                .filter(Boolean)
                .map((value) => String(value).toLowerCase());

            if (searchFields.some((value) => value.includes(lower))) {
                matches.push(id);
            }
        });

        setTreeSearchMatches(matches);
        setTreeSearchIndex(0);
    }, [treeSearchTerm, assemblyIndex, products]);

    useEffect(() => {
        if (!assemblyIndex || !treeSearchMatches.length) return;

        const { parentById } = assemblyIndex;
        const activeId =
            treeSearchMatches[treeSearchIndex] || treeSearchMatches[0];

        // Ensure ancestors of active match are expanded
        setExpanded((prev) => {
            const toExpand = new Set(prev);
            let current = activeId;
            while (parentById.has(current)) {
                const parentId = parentById.get(current);
                toExpand.add(parentId);
                current = parentId;
            }
            return Array.from(toExpand);
        });

        // Scroll to active match
        const el = treeItemRefs.current.get(activeId);
        if (el) {
            el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
    }, [treeSearchMatches, treeSearchIndex, assemblyIndex]);

    const handleTreeSearchSubmit = () => {
        if (!treeSearchMatches.length) return;

        setTreeSearchIndex((prev) => {
            if (treeSearchMatches.length <= 1) return 0;
            return (prev + 1) % treeSearchMatches.length; // cycle
        });
    };

    const collectProductRefsFromNode = (node, targetSet) => {
        if (!node || !targetSet) return;

        if (node.productRef !== null && node.productRef !== undefined) {
            targetSet.add(String(node.productRef));
        }

        if (Array.isArray(node.children)) {
            node.children.forEach((child) =>
                collectProductRefsFromNode(child, targetSet)
            );
        }
    };

    const applyVisibilityForProductRefs = (productRefs = [], visible) => {
        if (!assemblyIndex || !sceneRef.current) return false;
        const refs = Array.from(
            new Set((Array.isArray(productRefs) ? productRefs : [productRefs]).map((r) => String(r)).filter(Boolean))
        );
        if (!refs.length) return false;

        sceneRef.current?.setAssemblyNodesVisibility?.(refs, visible);

        const { productRefToNodeIds } = assemblyIndex;
        setNodeVisibility((prev) => {
            const next = { ...prev };
            refs.forEach((ref) => {
                const nodeIds = productRefToNodeIds.get(String(ref));
                if (!nodeIds) return;
                nodeIds.forEach((id) => {
                    next[id] = visible;
                });
            });
            return next;
        });
        return true;
    };

    const unhideLastTreeHiddenSet = () => {
        const stack = hiddenPartsFiloStackRef.current;
        if (!stack.length) return false;
        const refs = stack.pop();
        return applyVisibilityForProductRefs(refs, true);
    };

    const handlePartDoubleClickHide = (partMeta) => {
        const selectionMeta = partMeta || selectedPart;
        if (!selectionMeta || !assemblyIndex) return;

        const refsToHide = new Set();
        const node = selectionMeta?.assemblyNode;
        if (node) {
            collectProductRefsFromNode(node, refsToHide);
            if ((node.productRef !== null && node.productRef !== undefined) && refsToHide.size === 0) {
                refsToHide.add(String(node.productRef));
            }
        }

        if (refsToHide.size === 0 && selectionMeta?.name) {
            const matched = findMatchingProductForPart(selectionMeta.name, selectionMeta?.assemblyNode?.productRef);
            const refs = matched ? getProductRefsForProduct(matched) : [];
            refs.forEach((ref) => refsToHide.add(String(ref)));
        }

        const refs = Array.from(refsToHide);
        if (!refs.length) return;

        const didHide = applyVisibilityForProductRefs(refs, false);
        if (didHide) {
            hiddenPartsFiloStackRef.current.push(refs);
        }

        skipMatrixApplyOnNextDeselectRef.current = true;
        sceneRef.current?.clearSelection?.();
    };

    const handleNodeCheckboxChange = (nodeId, checked) => {
        if (!assemblyIndex) return;

        const { nodeById } = assemblyIndex;
        const rootNode = nodeById.get(nodeId);
        if (!rootNode) return;

        const newVis = {};
        const productRefsToToggle = new Set();

        const applyToSubtree = (node) => {
            if (!node) return;
            const id = node._id;
            newVis[id] = checked;

            if (node.productRef !== null && node.productRef !== undefined) {
                productRefsToToggle.add(String(node.productRef));
            }

            if (Array.isArray(node.children)) {
                node.children.forEach(applyToSubtree);
            }
        };

        applyToSubtree(rootNode);

        if (productRefsToToggle.size > 0) {
            sceneRef.current?.setAssemblyNodesVisibility?.(
                Array.from(productRefsToToggle),
                checked
            );
        } else {
            // Even if no explicit productRef was found, force a sync so parents update
            sceneRef.current?.setAssemblyNodesVisibility?.([], checked);
        }

        setNodeVisibility((prev) => ({
            ...prev,
            ...newVis,
        }));
    };

    const toggleNodeExpanded = (nodeId) => {
        setExpanded((prev) =>
            prev.includes(nodeId)
                ? prev.filter((id) => id !== nodeId)
                : [...prev, nodeId]
        );
    };

    const handleNodeClick = (nodeId) => {
        setSelectedNodeId(nodeId);
        setModelVisible(true);
        setNon3DMessageVisible(false);
        if (!assemblyIndex) return;
        const node = assemblyIndex.nodeById.get(nodeId);
        if (!node || !sceneRef.current) return;

        const refsToShow = new Set();
        collectProductRefsFromNode(node, refsToShow);
        const refs = Array.from(refsToShow);
        if (!refs.length) return;

        const selectedNodeRef =
            node?.productRef !== undefined && node?.productRef !== null
                ? String(node.productRef)
                : null;
        const preferredRef =
            selectedNodeRef && refs.includes(selectedNodeRef)
                ? selectedNodeRef
                : refs[0];

        const matched = findMatchingProductForPart(
            node?.name || '',
            node?.productRef
        );
        const occurrenceRef = preferredRef || (node?.productRef != null ? String(node.productRef) : null);
        const occurrenceCount = getOccurrenceCountForProductRef(occurrenceRef);
        const fallbackOccurrenceCount = Array.isArray(node?.objects) ? node.objects.length : 0;
        const effectiveOccurrenceCount = occurrenceCount || fallbackOccurrenceCount || 1;
        const hasMultipleOccurrences = effectiveOccurrenceCount > 1;
        if (matched) {
            setPartMenuData({
                part_name: matched.product_name || node?.name || "Selected Part",
                part_number: matched.product_number || node?.name || null,
                part_type: matched.ProductType || matched.ProductTypeKey || "N/A",
                product_id: matched.product_id || null,
                price: getSitePrice(matched),
                purchasable: matched.purchasable,
                isOrderable: true,
                availableIn3D: true,
                hasMultipleOccurrences,
                occurrenceCount: effectiveOccurrenceCount,
            });
        } else {
            const fallbackPart =
                node?.productRef != null ? String(node.productRef) : (node?.name || null);
            setPartMenuData({
                part_name: node?.name || "Selected Part",
                part_number: fallbackPart,
                part_type: null,
                product_id: null,
                price: null,
                purchasable: 0,
                isOrderable: false,
                availableIn3D: true,
                hasMultipleOccurrences,
                occurrenceCount: effectiveOccurrenceCount,
            });
        }
        setShowPartMenu(true);
        setShowMultiPartMenu(false);
        setSelectedPart({
            name: node?.name || "Selected Part",
            uuid: `tree-${nodeId}`,
            type: "AssemblyNode",
            userData: {},
            material: null,
            object3D: null,
            assemblyNode: node,
        });
        setSelectedParts([]);

        sceneRef.current?.selectAssemblySubtreeByProductRefs?.(refs, {
            primaryProductRef: preferredRef,
            focus: false,
            emitSelectionCallbacks: false,
            includeAllOccurrences: false,
        });
    };

    const onTreeNodeClick = (nodeId) => {
        handleNodeClick(nodeId);
    };

    const filteredTree = useMemo(() => {
        if (!assemblyTree) return null;
        if (!searchText) return assemblyTree;
        return filterTreeBySearch(assemblyTree, searchText);
    }, [assemblyTree, searchText]);

    const renderTreeItems = (node, level = 0) => {
        if (!node) return null;

        const nodeId = node._id || 'root';
        const { partName, partNumber, description } = resolveTreeNodeMeta(node);
        const checked = nodeVisibility[nodeId] ?? true;

        const hasChildren =
            Array.isArray(node.children) && node.children.length > 0;
        const isExpanded = expanded.includes(nodeId);
        const isSelected = selectedNodeId === nodeId;

        const isSearchMatch =
            !!treeSearchTerm && treeSearchMatches.includes(nodeId);
        const isActiveSearchMatch =
            isSearchMatch &&
            treeSearchMatches[treeSearchIndex] === nodeId;

        return (
            <Box key={nodeId}>
                <Box
                    ref={(el) => {
                        if (el) {
                            treeItemRefs.current.set(nodeId, el);
                        }
                    }}
                    sx={(theme) => {
                        const base = {
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            pl: 1 + level * 2.0,
                            pr: 0.5,
                            py: 0.25,
                            borderRadius: 0.75,
                            cursor: 'pointer',
                            minHeight: 44,
                        };

                        let bgcolor = 'transparent';
                        let color = theme.palette.text.primary;

                        const primaryLight = theme.palette.primary.light;
                        const warningLight = theme.palette.warning.light;

                        if (isSelected) {
                            // MAIN SELECTION HIGHLIGHT (PRIMARY)
                            bgcolor = alpha(primaryLight, 0.2);
                            color = theme.palette.primary.main;
                        } else if (isActiveSearchMatch) {
                            // current “jumped to” SEARCH MATCH (WARNING)
                            bgcolor = alpha(warningLight, 0.18);
                        } else if (isSearchMatch) {
                            // other SEARCH MATCHES (WARNING, softer)
                            bgcolor = alpha(warningLight, 0.08);
                        }

                        const hoverBg = (() => {
                            if (isSelected) {
                                return alpha(primaryLight, 0.25);
                            }
                            if (isActiveSearchMatch || isSearchMatch) {
                                return alpha(warningLight, 0.16);
                            }
                            // generic hover if not selected / match
                            return alpha(theme.palette.action.hover, 0.1);
                        })();

                        return {
                            ...base,
                            bgcolor,
                            color,
                            '&:hover': {
                                bgcolor: hoverBg,
                            },
                        };
                    }}
                    onClick={() => onTreeNodeClick(nodeId)}
                >
                    {hasChildren ? (
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleNodeExpanded(nodeId);
                            }}
                            sx={{ mr: 0.25 }}
                        >
                            {isExpanded ? (
                                <ExpandMoreIcon fontSize="inherit" />
                            ) : (
                                <ChevronRightIcon fontSize="inherit" />
                            )}
                        </IconButton>
                    ) : (
                        <Box sx={{ width: 28, flexShrink: 0 }} />
                    )}

                    <Checkbox
                        size="small"
                        checked={checked}
                        onChange={(e) => {
                            e.stopPropagation();
                            handleNodeCheckboxChange(
                                nodeId,
                                e.target.checked
                            );
                        }}
                        onClick={(e) => e.stopPropagation()}
                    />

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            minWidth: 0,
                        }}
                    >
                        <Typography
                            variant="body1"
                            noWrap
                            sx={{
                                fontWeight: isSelected ? 600 : 400,
                            }}
                        >
                            {partName}
                        </Typography>
                        {(partNumber || description) && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                                sx={{ fontSize: 11 }}
                            >
                                {partNumber ? `Part #: ${partNumber}` : ''}
                                {description ? ` / ${description}` : ''}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {hasChildren && isExpanded && (
                    <Box>
                        {node.children.map((child) =>
                            renderTreeItems(child, level + 1)
                        )}
                    </Box>
                )}
            </Box>
        );
    };

    // ------- subassemblies visibility helper --------

    const applySubAssemblyVisibility = (selectedIds) => {
        if (!assemblyIndex || !sceneRef.current) return;
        const { nodeById, rootId } = assemblyIndex;
        const rootNode = nodeById.get(rootId);
        if (!rootNode || !Array.isArray(rootNode.children)) return;

        const visibilityPatch = {};
        const refsToShow = new Set();
        const refsToHide = new Set();

        const applyToSubtree = (node, visible) => {
            if (!node) return;
            visibilityPatch[node._id] = visible;

            const targetSet = visible ? refsToShow : refsToHide;
            collectProductRefsFromNode(node, targetSet);

            if (Array.isArray(node.children)) {
                node.children.forEach((child) =>
                    applyToSubtree(child, visible)
                );
            }
        };

        const showAll = !selectedIds || selectedIds.length === 0;

        rootNode.children.forEach((child) => {
            const isSelected = showAll
                ? true
                : selectedIds.includes(child._id);
            applyToSubtree(child, isSelected);
        });

        if (refsToShow.size > 0) {
            sceneRef.current?.setAssemblyNodesVisibility?.(
                Array.from(refsToShow),
                true
            );
        }

        if (refsToHide.size > 0) {
            sceneRef.current?.setAssemblyNodesVisibility?.(
                Array.from(refsToHide),
                false
            );
        }

        if (refsToShow.size === 0 && refsToHide.size === 0) {
            sceneRef.current?.setAssemblyNodesVisibility?.([], true);
        }

        setNodeVisibility((prev) => ({
            ...prev,
            ...visibilityPatch,
        }));
    };

    const handleSubAssemblyCardClick = (event, nodeId, nodeData) => {
        if (!assemblyIndex || !sceneRef.current) return;
        setModelVisible(true);
        setNon3DMessageVisible(false);
        const isModifier = event.ctrlKey || event.metaKey;
        setSelectVisibleActive(false);
        let newSelectedIds;
        if (isModifier) {
            const current = selectedSubAssemblyIds;
            if (current.includes(nodeId)) {
                newSelectedIds = current.filter((id) => id !== nodeId);
            } else {
                newSelectedIds = [...current, nodeId];
            }
        } else {
            setSelectedPart(null);
            clearSelectionActionToggles();
            setSelectedParts(null);
            newSelectedIds = [nodeId];
        }

        setSelectedSubAssemblyIds(newSelectedIds);
        applySubAssemblyVisibility(newSelectedIds);

        if (!isModifier && newSelectedIds.length === 1) {
            const selectedNode = nodeData || assemblyIndex.nodeById.get(nodeId);
            if (!selectedNode) return;

            const matched = findMatchingProductForPart(
                selectedNode?.name,
                selectedNode?.productRef
            );
            if (matched) {
                setPartMenuData({
                    part_name: matched.product_name || selectedNode?.name || "Selected Subassembly",
                    part_number: matched.product_number || selectedNode?.name || null,
                    part_type: matched.ProductType || matched.ProductTypeKey || "N/A",
                    product_id: matched.product_id || null,
                    price: getSitePrice(matched),
                    purchasable: matched.purchasable,
                    isOrderable: true,
                    availableIn3D: true,
                });
            } else {
                const fallbackPart = selectedNode?.productRef != null
                    ? String(selectedNode.productRef)
                    : (selectedNode?.name || null);
                setPartMenuData({
                    part_name: selectedNode?.name || "Selected Subassembly",
                    part_number: fallbackPart,
                    part_type: null,
                    product_id: null,
                    price: null,
                    purchasable: 0,
                    isOrderable: false,
                    availableIn3D: true,
                });
            }
            setShowPartMenu(true);
            setShowMultiPartMenu(false);
            setSelectedNodeId(nodeId);
            setSelectedPart({
                name: selectedNode?.name || "Selected Subassembly",
                uuid: `subassembly-${nodeId}`,
                type: "SubAssembly",
                userData: {},
                material: null,
                object3D: null,
                assemblyNode: selectedNode,
            });
            setSelectedParts([]);

            const refsToShow = new Set();
            collectProductRefsFromNode(selectedNode, refsToShow);
            const refs = Array.from(refsToShow);
            const selectedNodeRef =
                selectedNode?.productRef !== undefined &&
                    selectedNode?.productRef !== null
                    ? String(selectedNode.productRef)
                    : null;
            const preferredRef =
                selectedNodeRef && refs.includes(selectedNodeRef)
                    ? selectedNodeRef
                    : null;

            if (refs.length > 0) {
                sceneRef.current?.clearGlobalTransparency?.();
                sceneRef.current?.showOnlyProductRefs?.(refs, {
                    emptyMode: "hideAll",
                    focus: false,
                });
                sceneRef.current?.selectAssemblySubtreeByProductRefs?.(refs, {
                    primaryProductRef: preferredRef || refs[0],
                    focus: true,
                    emitSelectionCallbacks: false,
                });
                setIsolateSelectionToggle(true);
            }
        }
    };

    // ------- context menu: unhide last hidden --------
    const handleCanvasContextMenu = (e) => {
        const target = e.target;
        if (!(target instanceof HTMLElement)) return;

        const isCanvas = !!target.closest('canvas');
        if (!isCanvas) return;

        e.preventDefault();
        unhideLastTreeHiddenSet();
    };

    // ---------- top / side UI handlers ----------

    const handleTabChange = (_event, newValue) => {
        setTabValue(newValue);
        if (newValue === 1) {
            // show loader immediately
            setRsplLoading(true);
            setRsplReady(false);

            // defer RSPL heavy rendering so UI paints instantly
            setTimeout(() => {
                setRsplReady(true);
                setRsplLoading(false);
            }, 100);
        }
    };

    const [moreAnchorEl, setMoreAnchorEl] = useState(null);
    const isMoreMenuOpen = Boolean(moreAnchorEl);

    const handleMoreClick = (event) => {
        setMoreAnchorEl(event.currentTarget);
    };

    const handleMoreClose = () => {
        setMoreAnchorEl(null);
    };

    const handleSettings = () => {
        setSettingsOpen(true);
        handleMoreClose();
    };

    const handleShadedView = () => {
        // TODO: wire to material change if desired
        handleMoreClose();
    };

    const handleShadedWireView = () => {
        // TODO: wire to material change if desired
        handleMoreClose();
    };

    const handleMachineTransparencyToggle = () => {
        setMachineTransparentToggle((prev) => !prev);
    };

    const handlePartTransparencyToggle = () => {
        if (!selectedPart) return;
        setPartTransparencyToggle((prev) => !prev);
    };

    const handleIsolateToggle = () => {
        if (!selectedPart) return;
        setIsolateSelectionToggle((prev) => {
            const next = !prev;
            if (!sceneRef.current) return next;

            if (next) {
                const selectedRef =
                    selectedPart?.assemblyNode?.productRef !== undefined &&
                        selectedPart?.assemblyNode?.productRef !== null
                        ? String(selectedPart.assemblyNode.productRef)
                        : null;

                // Ensure ModelScene has a valid current selection root for synthetic
                // RSPL/Subassembly selections before isolate is applied.
                if (selectedRef) {
                    sceneRef.current?.selectAssemblySubtreeByProductRefs?.([selectedRef], {
                        primaryProductRef: selectedRef,
                        focus: false,
                        emitSelectionCallbacks: false,
                        includeAllOccurrences: false,
                    });
                }

                sceneRef.current?.isolateSelected?.({ focus: false });
                return next;
            }

            // Toggle OFF isolate: restore the non-isolated matrix view immediately.
            const shouldRespectRsplFilter =
                !machineTransparentToggle && tabValue === 1 && !!rsplReady;
            const filterRefs = shouldRespectRsplFilter ? currentRsplProductRefs : [];

            sceneRef.current?.showOnlyProductRefs?.(filterRefs, {
                emptyMode: shouldRespectRsplFilter ? "hideAll" : "showAll",
                focus: false,
            });

            if (machineTransparentToggle) {
                sceneRef.current?.showOnlyProductRefs?.([], { focus: false });
                sceneRef.current?.showMachineTransparentExceptSelected?.({ opacity: 0.15 });
            }

            return next;
        });
    };

    useEffect(() => {
        if (!sceneRef.current) return;
        if (skipMatrixApplyOnNextDeselectRef.current && !selectedPart) {
            skipMatrixApplyOnNextDeselectRef.current = false;
            return;
        }

        const shouldRespectRsplFilter =
            !machineTransparentToggle && tabValue === 1 && !!rsplReady;
        const filterRefs = shouldRespectRsplFilter ? currentRsplProductRefs : [];
        const shouldApplyMachineTransparent =
            machineTransparentToggle && !isolateSelectionToggle;

        if (!shouldApplyMachineTransparent) {
            sceneRef.current?.clearGlobalTransparency?.();
        }
        sceneRef.current?.showOnlyProductRefs?.(filterRefs, {
            emptyMode: shouldRespectRsplFilter ? "hideAll" : "showAll",
            focus: false,
        });

        if (selectedPart && isolateSelectionToggle) {
            sceneRef.current?.isolateSelected?.({ focus: false });
        }

        if (shouldApplyMachineTransparent) {
            sceneRef.current?.showOnlyProductRefs?.([], { focus: false });
            sceneRef.current?.showMachineTransparentExceptSelected?.({ opacity: 0.15 });
        }

        sceneRef.current?.makeSelectedSolid?.();
        if (
            selectedPart &&
            (partTransparencyToggle || (isolateSelectionToggle && machineTransparentToggle))
        ) {
            sceneRef.current?.makeSelectedTransparent?.();
        }
    }, [
        selectedPart,
        machineTransparentToggle,
        partTransparencyToggle,
        isolateSelectionToggle,
        tabValue,
        rsplReady,
        currentRsplProductRefs,
    ]);

    useEffect(() => {
        if (!modelLoaded) return;
        skipMatrixApplyOnNextDeselectRef.current = false;
        hiddenPartsFiloStackRef.current = [];
        sceneRef.current?.resetModel?.();
        sceneRef.current?.clearSelection?.();
        sceneRef.current?.clearGlobalTransparency?.();
        sceneRef.current?.showOnlyProductRefs?.([]);
        setSelectedPart(null);
        setSelectedParts(null);
        setShowPartMenu(false);
        setShowMultiPartMenu(false);
        setMachineTransparentToggle(false);
        setPartTransparencyToggle(false);
        setIsolateSelectionToggle(false);
        setOccurrenceToggle(false);
        setSelectVisibleActive(false);
    }, [modelLoaded]);

    const handleBom = () => {
        const bomRows = buildBomRows();
        setBomData(bomRows);
        setBomOpen(true);
        handleMoreClose();
    };

    const handleSettingsClose = () => {
        setSettingsOpen(false);
    };

    const handleBomClose = () => {
        setBomOpen(false);
    };

    const handleCartClick = () => {
        setCartOpen((prev) => !prev);
    };

    const resetModel = () => {
        // Reset 3D model & remove highlights
        skipMatrixApplyOnNextDeselectRef.current = false;
        hiddenPartsFiloStackRef.current = [];
        sceneRef.current?.resetModel?.();
        sceneRef.current?.clearSelection?.();
        sceneRef.current?.clearGlobalTransparency?.();
        sceneRef.current?.showOnlyProductRefs?.([]);
        setModelVisible(true);
        setNon3DMessageVisible(false);
        setNon3DPartNumber("");
        keepPartMenuOnNextClearRef.current = false;

        setSelectVisibleActive(false);
        // Close Part Menu
        setShowPartMenu(false);
        setPartMenuData(null);

        // Close Multi-Part menu
        setShowMultiPartMenu(false);
        setMultiPartSelection([]);
        setPartMenuDelta({ x: 0, y: 0 });
        setMultiPartMenuDelta({ x: 0, y: 0 });

        // Clear selected part & selection arrays
        setSelectedPart(null);
        setSelectedParts([]);

        // Reset toggles
        setOccurrenceToggle(false);
        setMachineTransparentToggle(false);
        setPartTransparencyToggle(false);
        setIsolateSelectionToggle(false);
        setActiveRsplPartNumber(null);

        // Reset tree visibility & expansions
        if (assemblyIndex) {
            const { nodeById, rootId } = assemblyIndex;

            const vis = {};
            nodeById.forEach((_node, id) => {
                vis[id] = true;
            });
            setNodeVisibility(vis);
            setExpanded([rootId]);
        }

        // Reset sub-assemblies
        setSelectedSubAssemblyIds([]);
        setSubSearchTerm('');

        // Reset tree search
        setTreeSearchTerm('');
        setTreeSearchMatches([]);
        setTreeSearchIndex(0);

        // Reset RSPL search state
        setRsplSearchTerm("");
        setRsplSearchResults([]);
        setRsplSearchIndex(0);

        // Reset explode
        setExplodeEnabled(false);
        setExplodeValue(0.5);
        setSettings((prev) => ({
            ...prev,
            explodeEnabled: false,
            explodeValue: 0.5,
        }));
    };

    const buildBomRows = () => {
        if (!assemblyTree) return [];

        const quantityMap = new Map();

        const traverse = (node) => {
            if (!node) return;
            const partName = node.name?.trim();

            if (partName) {
                if (!quantityMap.has(partName)) {
                    quantityMap.set(partName, 0);
                }
                quantityMap.set(partName, quantityMap.get(partName) + 1);
            }

            if (Array.isArray(node.children)) {
                node.children.forEach(traverse);
            }
        };

        traverse(assemblyTree);

        const rows = [];

        quantityMap.forEach((qty, name) => {
            const product = findMatchingProductForPart(name);

            rows.push({
                id: name,
                part_name: product?.product_name || "",
                part_number: product?.product_number || name,
                quantity: qty,
                price:
                    product && product.purchasable
                        ? getSitePrice(product)
                        : "",
            });
        });

        return rows;
    };

    const bomColumns = [
        { field: "part_name", headerName: t('TABLE_HEADERS.PART_NAME'), flex: 1 },
        { field: "part_number", headerName: t('TITLES.PART_NUMBER_1'), flex: 1 },
        { field: "quantity", headerName: t('TABLE_HEADERS.QUANTITY'), width: 150 },
        { field: "price", headerName: t('TABLE_HEADERS.PRICE'), width: 150 },
    ];



    const showParentFromSelection = () => {
        // New behavior: hide everything except current selection and
        // progressively reveal parents via ModelScene's stepping logic
        sceneRef.current?.stepShowParentForSelection?.();
    };

    const rsplRenderedByType = useMemo(() => {
        const map = {};

        // Per-type lists
        productTypes.forEach((type) => {
            const key = normalizeRsplType(type);
            const items = productsByType[key] ?? [];

            map[type] = items
                .map((prod) => {
                    const in3D = !!product3DMap[prod.product_number];
                    const isWarning = rsplSearchResults.some(
                        (r) => r.product_number === prod.product_number
                    );

                return (
                    <Box
                        id={`rspl-${prod.product_number}`}
                        key={prod.product_number}
                        sx={{
                            cursor: "pointer",
                            borderRadius: "6px",
                        }}
                        onClick={() => handleRSPLPartClick(prod)}
                    >
                        <MemoPartCard
                            id={`rspl-${prod.product_number}`}
                            showWarning
                            isActive={activeRsplPartNumber === prod.product_number}
                            warning={isWarning}
                            data={{
                                part_name: prod.product_name,
                                part_number: prod.product_number,
                                purchasable: prod.purchasable,
                                price: getSitePrice(prod),
                                availableIn3D: in3D,
                                productImageURL: prod.productImageURL,
                            }}
                        />
                    </Box>
                );
            });
        });

        // "All" = union of all productTypes (no "Other" since productTypes already excludes it)
        const allItemsMap = new Map(); // dedupe by product_number

        productTypes.forEach((type) => {
            const key = normalizeRsplType(type);
            const items = productsByType[key] ?? [];
            items
                .forEach((prod) => {
                    if (!allItemsMap.has(prod.product_number)) {
                        allItemsMap.set(prod.product_number, prod);
                    }
                });
        });

        map["All"] = Array.from(allItemsMap.values()).map((prod) => {
            const in3D = !!product3DMap[prod.product_number];
            const isWarning = rsplSearchResults.some(
                (r) => r.product_number === prod.product_number
            );

            return (
                <Box
                    id={`rspl-${prod.product_number}`}
                    key={prod.product_number}
                    sx={{
                        cursor: "pointer",
                        borderRadius: "6px",
                    }}
                    onClick={() => handleRSPLPartClick(prod)}
                >
                    <MemoPartCard
                        id={`rspl-${prod.product_number}`}
                        showWarning
                        isActive={activeRsplPartNumber === prod.product_number}
                        warning={isWarning}
                        data={{
                            part_name: prod.product_name,
                            part_number: prod.product_number,
                            purchasable: prod.purchasable,
                            price: getSitePrice(prod),
                            availableIn3D: in3D,
                            productImageURL: prod.productImageURL,
                        }}
                    />
                </Box>
            );
        });

        return map;
    }, [
        products,
        productTypes,
        productsByType,
        product3DMap,
        activeRsplPartNumber,
        rsplSearchResults,
        handleRSPLPartClick,
    ]);


    const handleCloseModel = () => {
        Swal.fire({
            title: t('UI_TEXT.ARE_YOU_SURE_YOU_WANT_TO_CLOSE'),
            text: t('UI_TEXT.ANY_ITEMS_ADDED_IN_THE_MODEL_CART_WILL_NOT_BE_ADDED_TO_THE_APPLICATION_CART'),
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: t('BUTTON.YES_CLOSE'),
            cancelButtonText: t('BUTTON.CANCEL'),

        }).then((result) => {
            if (result.isConfirmed) {
                navigate(returnPath);
            }
        });
    };

    const handleHardRefresh = () => {
        const url = new URL(window.location.href);
        url.searchParams.set('_hard_refresh', `${Date.now()}`);
        window.location.replace(url.toString());
    };

    const handleContactCcrClick = () => {
        setHelpOpen(false);
        setCcrOpen(true);
    };

    const helpSections = [
        {
            title: "Hamburger Menu",
            items: [
                {
                    icon: <MenuIcon fontSize="small" />,
                    title: "Menu Panel",
                    description:
                        "Open or close the left panel.\nSwitch between Sub Assemblies, RSPL Parts, and BOM.",
                },
            ],
        },
        {
            title: "Part Menu",
            items: [
                {
                    icon: <Launch fontSize="small" />,
                    title: "Open Part",
                    description:
                        "Open the selected part's product page.\nAvailable when a link is provided.",
                },
                {
                    icon: <Close fontSize="small" />,
                    title: "Close Menu",
                    description:
                        "Dismiss the part menu popover.\nSelection stays active in the model.",
                },
                {
                    icon: <ShoppingCart fontSize="small" />,
                    title: t('BUTTONS.ADD_TO_CART'),
                    description:
                        "Add the selected part to the model cart.\nAdjust quantities in the cart drawer.",
                },
                {
                    icon: <Summarize fontSize="small" />,
                    title: "Add to Request",
                    description:
                        "Send non-purchasable parts to the request list.\nSubmit with other parts later.",
                },
                {
                    icon: <KeyboardDoubleArrowUp fontSize="small" />,
                    title: "Show Parent",
                    description:
                        "Reveal the selected part's parent context.\nHelps you see where it lives.",
                },
                {
                    icon: <NorthEast fontSize="small" />,
                    title: "Isolate",
                    description:
                        "Hide everything except the selected part.\nGreat for focused inspection.",
                },
                {
                    icon: (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                            <LibraryAddCheck fontSize="small" />
                            <CheckBoxIcon fontSize="small" />
                        </Box>
                    ),
                    title: "Select Occurrences",
                    description:
                        "Toggle between one instance and all occurrences.\nUseful for repeated parts.",
                },
            ],
        },
        {
            title: "Bottom Toolbar",
            items: [
                {
                    control: (
                        <Button variant="modelMenuButtons">
                            <HomeIcon />
                        </Button>
                    ),
                    title: "Home",
                    description:
                        "Reset the model view and selections.\nRestores default visibility.",
                },

                {
                    control: (
                        <Button
                            variant="modelMenuButtons"
                            sx={(theme) =>
                                explodeEnabled
                                    ? {
                                        backgroundColor: theme.palette.primary.main,
                                        color: '#fff',
                                    }
                                    : {}
                            }
                        >
                            <OpenInFull />
                        </Button>
                    ),
                    title: "Explode Model",
                    description:
                        "Spread parts apart for clarity.\nUse the explode slider when enabled.",
                },
                {
                    control: (
                        <Button variant="modelMenuButtons" disabled={!selectedPart}>
                            <ZoomInIcon />
                        </Button>
                    ),
                    title: "Zoom to Selected",
                    description:
                        "Frame the selected part in view.\nDisabled until a part is selected.",
                },

                {
                    control: (
                        <Button
                            variant="modelMenuButtons"
                            sx={(theme) =>
                                selectVisibleActive
                                    ? {
                                        backgroundColor: theme.palette.primary.main,
                                        color: "#fff",
                                    }
                                    : {}
                            }
                        >
                            <CheckBoxIcon />
                        </Button>
                    ),
                    title: "Select Visible",
                    description:
                        "Select all currently visible parts.\nClick again to clear.",
                },
                {
                    control: (
                        <Button variant="modelMenuButtons" disabled={!selectedPart}>
                            <KeyboardDoubleArrowUp />
                        </Button>
                    ),
                    title: "Parent",
                    description:
                        "Reveal parent assemblies in the current view.\nDisabled until a part is selected.",
                },
                {
                    control: (
                        <Button variant="modelMenuButtons">
                            <QuestionMark />
                        </Button>
                    ),
                    title: "Help",
                    description:
                        "Open this guide.\nQuick reference for controls.",
                },
                {
                    control: (
                        <Button variant="modelMenuButtons">
                            <MoreVertIcon />
                        </Button>
                    ),
                    title: "More",
                    description:
                        "Open additional viewer actions.\nBOM and extra options live here.",
                },
                  {
                    control: (
                        <Button
                            variant="modelMenuButtons"
                            disabled={!selectedPart}
                            endIcon={<NorthEast fontSize="small" />}
                            sx={(theme) => ({
                                width: 'auto',
                                minWidth: 'fit-content',
                                px: 1.5,
                                borderRadius: '15px',
                                textTransform: "none",
                                whiteSpace: 'nowrap',
                                overflow: 'visible',
                                ...(isolateSelectionToggle
                                    ? {
                                        backgroundColor: theme.palette.primary.main,
                                        color: "#fff",
                                    }
                                    : {}),
                            })}
                        >
                            Isolate
                        </Button>
                    ),
                    title: "Isolate",
                    description:
                        "Show only selected part(s).\nDisabled until a part is selected.",
                },
                {
                    control: (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Button variant="modelMenuButtons">
                                <OpenWithIcon />
                            </Button>
                            <Typography variant="caption" color="text.secondary">/</Typography>
                             <Button
                                 variant="modelMenuButtons"
                                 sx={(theme) => ({
                                    backgroundColor: theme.palette.primary.main,
                                    color: "#fff",
                                })}
                            >
                                <Loop />
                            </Button>
                        </Box>
                    ),
                    title: "Rotate / Pan Toggle",
                    description:
                        "Switch drag behavior between rotate and pan.\nActive state means Pan mode.",
                },
               
                {
                    control: (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Button
                                variant="modelMenuButtons"
                                sx={{
                                    width: 'auto',
                                    minWidth: 'fit-content',
                                    px: 1.5,
                                    borderRadius: '15px',
                                    textTransform: "none",
                                    whiteSpace: 'nowrap',
                                    overflow: 'visible',
                                    gap: 0.5,
                                }}
                            >
                                Part
                                <PiDropSimpleFill size={14} />
                            </Button>
                            <Typography variant="caption" color="text.secondary">/</Typography>
                            <Button
                                variant="modelMenuButtons"
                                sx={(theme) => ({
                                    width: 'auto',
                                    minWidth: 'fit-content',
                                    px: 1.5,
                                    borderRadius: '15px',
                                    textTransform: "none",
                                    whiteSpace: 'nowrap',
                                    overflow: 'visible',
                                    gap: 0.5,
                                    backgroundColor: theme.palette.primary.main,
                                    color: "#fff",
                                })}
                            >
                                Part
                                <BsTransparency size={14} />
                            </Button>
                        </Box>
                    ),
                    title: "Part Transparent / Solid",
                    description:
                        "Toggle selected-part transparency.\nDisabled until a part is selected.",
                },
               {
                    control: (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Button
                                variant="modelMenuButtons"
                                sx={{
                                    width: 'auto',
                                    minWidth: 'fit-content',
                                    px: 1.5,
                                    borderRadius: '15px',
                                    textTransform: "none",
                                    whiteSpace: 'nowrap',
                                    overflow: 'visible',
                                    gap: 0.5,
                                }}
                            >
                                Machine
                                <PiDropSimpleFill size={14} />
                            </Button>
                            <Typography variant="caption" color="text.secondary">/</Typography>
                            <Button
                                variant="modelMenuButtons"
                                sx={(theme) => ({
                                    width: 'auto',
                                    minWidth: 'fit-content',
                                    px: 1.5,
                                    borderRadius: '15px',
                                    textTransform: "none",
                                    whiteSpace: 'nowrap',
                                    overflow: 'visible',
                                    gap: 0.5,
                                    backgroundColor: theme.palette.primary.main,
                                    color: "#fff",
                                })}
                            >
                                Machine
                                <BsTransparency size={14} />
                            </Button>
                        </Box>
                    ),
                    title: "Machine Transparent / Solid",
                    description:
                        "Toggle full-machine transparency.\nWhen ON, machine becomes transparent and selected part behavior follows toolbar matrix rules.",
                },
            ],
        },
    ];

    const helpContent = (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                p: { xs: 0.5, md: 1 },
            }}
        >
            {helpSections.map((section) => {
                const isBottomToolbarSection = section.title === "Bottom Toolbar";
                return (
                    <Box
                        key={section.title}
                        sx={{
                            borderRadius: 2,
                            p: 1.5,
                        }}
                    >
                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.25 }}>
                            {section.title}
                        </Typography>

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: isBottomToolbarSection
                                    ? "1fr"
                                    : { xs: "1fr", md: "1fr 1fr" },
                                gap: 1,
                            }}
                        >
                            {section.items.map((item) => (
                                <Box
                                    key={item.title}
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: { xs: "1fr", sm: "max-content minmax(0,1fr)" },
                                        columnGap: 1.25,
                                        rowGap: 0.5,
                                        alignItems: "start",
                                        p: 1.25,

                                    }}
                                >
                                    <Box
                                        sx={{
                                            flexShrink: 0,
                                            pointerEvents: "none",
                                            width: "max-content",
                                            display: "flex",
                                            justifyContent: "flex-start",
                                            mt: { xs: 0, sm: 0.25 },
                                        }}
                                    >
                                        {item.control ? (
                                            item.control
                                        ) : (
                                            <Box
                                                sx={{
                                                    width: 36,
                                                    height: 36,
                                                    borderRadius: "50%",
                                                        backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.06),
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                }}
                                            >
                                                {item.icon}
                                            </Box>
                                        )}
                                    </Box>

                                    <Box sx={{ minWidth: 0, alignSelf: "center" }}>
                                        <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5, lineHeight: 1.2 }}>
                                            {item.title}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ whiteSpace: "pre-line", lineHeight: 1.55, display: "block" }}
                                        >
                                            {item.description}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );



    // ---------- layout ----------

    const containerStyles = {
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
        background: (theme) => theme.palette.background.paper,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
    };

    // "Select visible" handler
    const handleSelectVisibleParts = () => {
        // If already active → clicking again should disable & clear everything
        if (selectVisibleActive) {
            // Deactivate toggle
            setSelectVisibleActive(false);

            // Clear selections in the 3D scene
            sceneRef.current?.clearSelection?.();
            sceneRef.current?.resetModel?.();

            // Close UI menus
            setShowPartMenu(false);
            setShowMultiPartMenu(false);
            setMultiPartSelection([]);
            setSelectedPart(null);
            clearSelectionActionToggles();
            setSelectedParts([]);

            return;
        }

        // -------- ACTIVATING SELECT VISIBLE --------
        const result = sceneRef.current?.selectVisibleParts?.() || [];

        // Hide any single-part menu
        setShowPartMenu(false);

        if (Array.isArray(result) && result.length > 0) {
            const normalizeKey = (value) =>
                String(value ?? '')
                    .trim()
                    .toLowerCase();

            const dedupSelection = new Map();
            result.forEach((part, idx) => {
                const key =
                    part?.assemblyNode?.productRef !== undefined &&
                        part?.assemblyNode?.productRef !== null
                        ? `p:${part.assemblyNode.productRef}`
                        : `u:${part?.uuid ?? idx}`;
                if (!dedupSelection.has(key)) {
                    dedupSelection.set(key, part);
                }
            });

            if (assemblyIndex && level1SubAssemblies.length > 0) {
                const subAssemblyIdByKey = new Map();
                level1SubAssemblies.forEach(({ id, node }) => {
                    const keys = [
                        normalizeKey(node?.productRef),
                        normalizeKey(node?.name),
                    ].filter(Boolean);
                    keys.forEach((key) => {
                        if (!subAssemblyIdByKey.has(key)) {
                            subAssemblyIdByKey.set(key, new Set());
                        }
                        subAssemblyIdByKey.get(key).add(id);
                    });
                });

                const matchedSubAssemblyIds = new Set();
                result.forEach((part) => {
                    const candidateKeys = [
                        normalizeKey(part?.assemblyNode?.productRef),
                        normalizeKey(part?.assemblyNode?.name),
                        normalizeKey(part?.name),
                    ].filter(Boolean);

                    candidateKeys.forEach((key) => {
                        const matches = subAssemblyIdByKey.get(key);
                        if (matches) {
                            matches.forEach((id) => matchedSubAssemblyIds.add(id));
                        }
                    });
                });

                if (matchedSubAssemblyIds.size > 0) {
                    const refsToShow = new Set();

                    const collectSubtreeParts = (node) => {
                        if (!node) return;
                        if (node.productRef !== null && node.productRef !== undefined) {
                            const productRef = String(node.productRef);
                            refsToShow.add(productRef);
                            const key = `p:${productRef}`;
                            if (!dedupSelection.has(key)) {
                                dedupSelection.set(key, {
                                    name: node?.name || productRef,
                                    uuid: `subassembly-${node._id}`,
                                    type: 'AssemblyNode',
                                    assemblyNode: node,
                                });
                            }
                        }

                        if (Array.isArray(node.children)) {
                            node.children.forEach(collectSubtreeParts);
                        }
                    };

                    matchedSubAssemblyIds.forEach((subAssemblyId) => {
                        const subNode = assemblyIndex.nodeById.get(subAssemblyId);
                        collectSubtreeParts(subNode);
                    });

                    if (refsToShow.size > 0) {
                        sceneRef.current?.setAssemblyNodesVisibility?.(
                            Array.from(refsToShow),
                            true
                        );
                    }

                    setSelectedSubAssemblyIds((prev) =>
                        Array.from(new Set([...(prev || []), ...matchedSubAssemblyIds]))
                    );
                }
            }

            const finalSelection = Array.from(dedupSelection.values());
            setSelectedParts(finalSelection);
            setMultiPartSelection(finalSelection);
            setShowMultiPartMenu(true);
            setSelectVisibleActive(true);
        } else {
            setMultiPartSelection([]);
            setSelectedParts([]);
            setShowMultiPartMenu(false);
            setSelectVisibleActive(false);
        }
    };



    const handleCanvasPointerMissed = (event) => {
        if (typeof event?.delta === "number" && event.delta > 4) return;
        const clickDetail =
            event?.detail ??
            event?.nativeEvent?.detail ??
            event?.sourceEvent?.detail;
        // Ignore multi-click miss events that are part of double-click interactions.
        if (typeof clickDetail === "number" && clickDetail > 1) return;
        skipMatrixApplyOnNextDeselectRef.current = true;
        sceneRef.current?.clearSelection?.();
    };
    const handleMultipleAddToCart = () => {
        if (!multiPartSelection || multiPartSelection.length === 0) {
            Swal.fire({
                icon: "info",
                title: "<div style='text-align:center;width:100%;'>No Parts Selected</div>",
                html: "<div style='text-align:center;width:100%;'>There are no visible parts selected.</div>",
            });
            return;
        }

        let purchasableParts = [];
        let requestListParts = [];
        let nonOrderableParts = [];

        // Categorize each part
        multiPartSelection.forEach((part) => {
            const label = part.assemblyNode?.name || part.name || "";
            const matchedProduct = findMatchingProductForPart(
                label,
                part?.assemblyNode?.productRef
            );

            if (!matchedProduct) {
                nonOrderableParts.push(label);
                return;
            }

            const partData = {
                product_id: matchedProduct.product_id,
                part_number: matchedProduct.product_number,
                part_name: matchedProduct.product_name,
                price: getSitePrice(matchedProduct),
                purchasable: matchedProduct.purchasable,
            };

            if (matchedProduct.purchasable) purchasableParts.push(partData);
            else requestListParts.push(partData);
        });

        // Add to cart
        setCartItems((prev) => {
            const updated = [...prev];
            purchasableParts.forEach((p) => {
                if (!updated.find((i) => i.part_number === p.part_number)) {
                    updated.push({ ...p, qty: 1 });
                }
            });
            return updated;
        });

        // Add to request list
        setRequestItems((prev) => {
            const updated = [...prev];
            requestListParts.forEach((p) => {
                if (!updated.find((i) => i.part_number === p.part_number)) {
                    updated.push({ part_number: p.part_number, product_id: p.product_id });
                }
            });
            return updated;
        });

        // ---------- Swal Logic (INLINE CENTERED) ---------- //
        const hasPurchasable = purchasableParts.length > 0;
        const hasRequestable = requestListParts.length > 0;
        const hasNonOrderable = nonOrderableParts.length > 0;

        let icon = "success";
        let title = "Parts Processed";
        let html = `
        <div style="text-align:center;width:100%;">
            <b>Purchasable (Added to Cart):</b> ${purchasableParts.length}<br/>
            <b>Request List Items:</b> ${requestListParts.length}<br/>
            ${hasNonOrderable ? `<b>Non-Orderable Parts:</b> ${nonOrderableParts.length}` : ""}
        </div>
    `;

        if (!hasPurchasable && !hasRequestable && hasNonOrderable) {
            icon = "warning";
            title = "No Purchasable or Requestable Parts";
            html = `
            <div style="text-align:center;width:100%;">
                None of the selected parts can be added.<br/>
                <b>Non-Orderable Parts:</b> ${nonOrderableParts.length}
            </div>
        `;
        }

        Swal.fire({
            icon,
            title: `<div style="text-align:center;width:100%;">${title}</div>`,
            html,
            confirmButtonText: t('GENERAL.OK'),
        });

        setShowMultiPartMenu(false);
    };

    const handleAddAllProductType = (type) => {
        const typeKey = normalizeRsplType(type);
        const parts = productsByType[typeKey];

        if (!parts || parts.length === 0) {
            Swal.fire({
                icon: "info",
                title: `<div style='text-align:center;'>${t('UI_TEXT.NO_PARTS_FOUND')}</div>`,
                html: `<div style='text-align:center;'>${t('UI_TEXT.THERE_ARE_NO_RSPL_PARTS_FOR_TYPE', { type })}</div>`
            });
            return;
        }

        const purchasable = parts.filter(p => p.purchasable);
        const requestable = parts.filter(p => !p.purchasable);

        Swal.fire({
            icon: "question",
            title: `<div style='text-align:center;'>Add All ${type} Parts?</div>`,

            html: `
            <div style='text-align:center;'>
                <b>Purchasable:</b> ${purchasable.length}<br/>
                <b>Request List:</b> ${requestable.length}<br/><br/>
                ${t('UI_TEXT.ALL_SELECTED_PARTS_WILL_BE_ADDED_AS_LINE_ITEMS', {
                    defaultValue: 'All selected parts will be added as individual line items.'
                })}
            </div>
        `,
            showCancelButton: true,
            confirmButtonText: t('TABLE_HEADERS.ADD'),
            cancelButtonText: t('BUTTON.CANCEL')
        }).then((result) => {
            if (!result.isConfirmed) return;

            const normalizedParts = parts
                .map((part) => normalizePartMeta({
                    ...part,
                    part_number: part?.product_number,
                    product_id: part?.product_id,
                    price: getSitePrice(part),
                    purchasable: part?.purchasable,
                }))
                .filter((part) => !!part?.part_number && !!part?.product_id);

            const purchasableParts = normalizedParts.filter((part) => !!part.purchasable);
            const requestableParts = normalizedParts.filter((part) => !part.purchasable);

            const existingCart = new Set(cartItems.map((item) => String(item?.part_number || "").trim()));
            const existingRequest = new Set(requestItems.map((item) => String(item?.part_number || "").trim()));

            const cartToAdd = purchasableParts
                .filter((item) => {
                    const key = String(item?.part_number || "").trim();
                    if (!key || existingCart.has(key)) return false;
                    existingCart.add(key);
                    return true;
                })
                .map((item) => ({ ...item, qty: 1 }));

            const requestToAdd = requestableParts.filter((item) => {
                const key = String(item?.part_number || "").trim();
                if (!key || existingRequest.has(key)) return false;
                existingRequest.add(key);
                return true;
            });

            const cartAdded = cartToAdd.length;
            const requestAdded = requestToAdd.length;

            if (cartToAdd.length > 0) {
                setCartItems((prev) => [...prev, ...cartToAdd]);
            }
            if (requestToAdd.length > 0) {
                setRequestItems((prev) => [...prev, ...requestToAdd]);
            }

            Swal.fire({
                icon: "success",
                title: `<div style='text-align:center;'>${t('BUTTON.ADDED')}</div>`,
                html: `
                <div style='text-align:center;'>
                    <b>${t('UI_TEXT.PURCHASABLE_PARTS')}:</b> ${cartAdded}<br/>
                    <b>${t('UI_TEXT.NON_PURCHASABLE_PARTS')}:</b> ${requestAdded}
                </div>
                `,
                confirmButtonText: t('GENERAL.OK'),
            });
        });
    };





    // -------------------- CART + REQUEST HELPERS -------------------- //

    // CLEAR BOTH LISTS
    const handleClearAll = () => {
        setCartItems([]);
        setRequestItems([]);

        // Optional: Close drawer or show toast message
        console.log("Cart & Request List cleared.");
    };

    const handleClearCart = () => {
        setCartItems([]);
    };

    const handleClearRequestList = () => {
        setRequestItems([]);
    };


    // ORDER ITEMS HANDLER
    const handleOrderItems = async () => {
        if (orderingItems) return;
        console.log("Cart Items", cartItems, requestItems)
        if (cartItems.length === 0 && requestItems.length === 0) {
            Swal.fire({
                icon: "info",
                title: `<div style='text-align:center;width:100%;'>${t('GENERAL.NO_ITEMS_TO_ORDER')}</div>`,
                html: `<div style='text-align:center;width:100%;'>${t('GENERAL.CART_AND_REQUEST_LIST_ARE_EMPTY')}</div>`,
            });
            return;
        }
        setOrderingItems(true);
        try {

        const expandedCart = [];
        const expandedRequestList = [];

        const expandGroup = (group) => {
            const type = group.group_type;
            const typeKey = normalizeRsplType(type);
            const parts = Array.isArray(productsByType[typeKey]) ? productsByType[typeKey] : [];
            if (!parts.length) return;

            parts.forEach((p) => {
                const item = {
                    product_id: p.product_id,
                    part_number: p.product_number,
                };

                if (p.purchasable) {
                    expandedCart.push({
                        ...item,
                        price: getSitePrice(p),
                        qty: 1,
                    });
                } else {
                    expandedRequestList.push(item);
                }
            });
        };

        cartItems.forEach((item) => {
            if (item.is_group) {
                expandGroup(item);
            } else {
                expandedCart.push({
                    product_id: item.product_id,
                    part_number: item.part_number,
                    qty: item.qty,
                    price: item.price,
                });
            }
        });

        requestItems.forEach((item) => {
            if (item.is_group) {
                expandGroup(item);
            } else {
                expandedRequestList.push({
                    product_id: item.product_id,
                    part_number: item.part_number,
                });
            }
        });

        const cartByProductId = new Map();
        expandedCart
            .filter((item) => item?.product_id)
            .forEach((item) => {
                const productId = String(item.product_id);
                const qty = Number(item?.qty ?? 1) || 1;
                cartByProductId.set(productId, (cartByProductId.get(productId) || 0) + qty);
            });

        const requestByProductId = new Set(
            expandedRequestList
                .filter((item) => item?.product_id)
                .map((item) => String(item.product_id))
        );

        const rlProducts = Array.from(requestByProductId).map((productId) => `${productId}:1`).join(",");
        const cartProducts = Array.from(cartByProductId.entries()).map(([productId, qty]) => `${productId}:${qty}`).join(",");

        if (!rlProducts && !cartProducts) {
            Swal.fire({
                icon: "info",
                title: "<div style='text-align:center;width:100%;'>No Orderable Parts</div>",
                html: "<div style='text-align:center;width:100%;'>The selected items could not be translated into orderable products.</div>",
            });
            return;
        }

        let storedUser = null;
        try {
            const rawUser = sessionStorage.getItem("user");
            storedUser = rawUser ? JSON.parse(rawUser) : null;
        } catch (storageError) {
            console.error("Unable to read session user:", storageError);
        }

        const userId = user?.user_id || storedUser?.user_id;

        if (!userId || !selectedSite || !machine_id) {
            Swal.fire({
                icon: "warning",
                title: "<div style='text-align:center;width:100%;'>Missing Information</div>",
                html: "<div style='text-align:center;width:100%;'>User, site, or machine information is unavailable. Please refresh and try again.</div>",
            });
            return;
        }

        const requestPayload = {
            userId,
            selectedSite,
            AddToRequest: rlProducts,
            AddToCart: cartProducts,
            machine_id,
        };

        console.log("Model Viewer RL List:", rlProducts);
        console.log("Model Viewer Cart List:", cartProducts);

        try {
            const response = await addModelCartItems(requestPayload);

            if (response?.status === false) {
                throw new Error(response?.message || "Unable to process order.");
            }

            handleClearAll();
            setCartOpen(false);

            const destination = cartProducts ? "/cart" : "/requests";
            const destinationLabel = cartProducts ? "Cart" : "Request List";
            const totalCartItems = expandedCart.length;
            const totalRequestItems = expandedRequestList.length;
            const redirectSeconds = 5;
            let remainingSeconds = redirectSeconds;
            let countdownInterval = null;

            const redirectFromSwal = () => {
                if (destination === "/cart") {
                    const params = new URLSearchParams({
                        mvSource: "modelviewer",
                        mvCartCount: String(totalCartItems),
                        mvRequestCount: String(totalRequestItems),
                    });
                    const url = `${destination}?${params.toString()}`;
                    window.location.href = url;
                } else {
                    navigate(destination);
                }
            };

            Swal.fire({
                icon: "success",
                title: "<div style='text-align:center;width:100%;'>Items Submitted</div>",
                html: `
                    <div style='text-align:center;width:100%;'>
                        <b>Added to Cart:</b> ${totalCartItems}<br/>
                        <b>Added to Request List:</b> ${totalRequestItems}<br/><br/>
                        Redirecting to ${destinationLabel} in <b><span id="swal-cart-countdown">${remainingSeconds}</span>s</b>.
                    </div>
                `,
                showConfirmButton: false,
                allowOutsideClick: false,
                didOpen: () => {
                    const countdownEl = document.getElementById("swal-cart-countdown");
                    countdownInterval = setInterval(() => {
                        remainingSeconds -= 1;
                        if (remainingSeconds <= 0) {
                            clearInterval(countdownInterval);
                            countdownInterval = null;
                            Swal.close();
                            return;
                        }
                        if (countdownEl) {
                            countdownEl.textContent = remainingSeconds;
                        }
                    }, 1000);
                },
                willClose: () => {
                    if (countdownInterval) {
                        clearInterval(countdownInterval);
                        countdownInterval = null;
                    }
                }
            }).then(() => {
                redirectFromSwal();
            });
        } catch (error) {
            console.error("Error placing model order:", error);
            Swal.fire({
                icon: "error",
                title: "<div style='text-align:center;width:100%;'>Unable to Submit</div>",
                html: `<div style='text-align:center;width:100%;'>${error?.message || "Please try again later."}</div>`,
            });
        }
        } finally {
            setOrderingItems(false);
        }
    };



    // useEffect(() => {
    //     if (error === "Model Not Found") {
    //         const timer = setTimeout(() => {
    //             window.location.href = "/machines";
    //         }, 3000);

    //         return () => clearTimeout(timer);
    //     }
    // }, [error]);

    if (error === "Model Not Found") {
        return (
            <Box
                sx={{
                    height: "100vh",
                    width: '100vw',
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    px: 3,
                }}
            >
                <Box sx={{ fontSize: 80, color: "error.main", opacity: 0.9 }}>
                    <InfoOutlined fontSize="inherit" />
                </Box>

                <Typography variant="h4" fontWeight="bold" mt={2}>
                    Model Not Found
                </Typography>

                <Typography variant="body1" color="text.secondary" mt={1} maxWidth={420}>
                    The requested 3D model could not be located. It may not exist or may have been removed.
                </Typography>

                <Button
                    variant="contained"
                    size="large"
                    sx={{ mt: 2, px: 4 }}
                    onClick={() => navigate(returnPath)}
                >
                    Back to Machines
                </Button>
            </Box>
        );
    }

    return (
        <>
            <Box
                sx={{
                    position: 'relative',
                    height: '100vh',
                    width: '100vw',
                    overflow: 'hidden',
                }}
                onContextMenu={handleCanvasContextMenu}
            >
                {modelFilesExist && !error ? (
                    <>
                        <Canvas
                            camera={{ position: [0, 0, 5], fov: 45 }}
                            style={{ width: '100%', height: '100%', display: modelVisible ? "inherit" : "none" }}
                            onPointerMissed={handleCanvasPointerMissed}
                        >
                            <Suspense fallback={null}>
                                <ModelScene
                                    ref={sceneRef}
                                    glbPath={`${SERVER_BASE_URL}/stock-models/GLB/Models/${fileName}.glb`}
                                    assemblyPath={`${SERVER_BASE_URL}/stock-models/GLB/Boms/${fileName}.json`}
                                    onPartSelected={handlePartSelected}
                                    onPartDoubleClick={handlePartDoubleClickHide}
                                    onAssemblyMapped={setAssemblyTree}
                                    lightSettings={{ type: 'studio', intensity: settings.light }}
                                    onSelectionChange={setSelectedParts}
                                    onVisibilityChange={handleVisibilityChange}
                                    explodeFactor={explodeEnabled ? explodeValue : 0}
                                    onModelLoaded={() => setModelLoaded(true)}
                                    navMode={navMode}
                                    highlightColor={defaultHighlightColor}
                                    highlightOpacity={defaultHighlightOpacity}
                                    modelAxis={defaultUpAxis}
                                    modelOrientation={modelOrientation}
                                    defaultCameraCorner={defaultCameraCorner}
                                />
                            </Suspense>

                            {settings.showGizmo && (
                                <GizmoHelper key={`gizmo-${defaultUpAxis}-${modelOrientation?.left || 'legacy'}-${modelOrientation?.right || 'legacy'}`} alignment="bottom-left" margin={[80, 80]}>
                                    <GizmoViewcube />
                                    <GizmoViewport
                                        axisColors={['red', 'green', 'blue']}
                                        labels={gizmoAxisLabels}
                                        labelColor="black"
                                    />
                                </GizmoHelper>
                            )}
                        </Canvas>

                    </>
                ) : null}
                {/* 3D Canvas – always mounted so OrbitControls stay stable */}

                {non3DMessageVisible && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            background: "rgba(255,255,255,0.92)",
                            p: 2.5,
                            borderRadius: "10px",
                            boxShadow: "0 0 8px rgba(0,0,0,0.25)",
                            zIndex: 100
                        }}
                    >
                        <Typography variant="body1" sx={{ textAlign: "center" }}>
                            <b>{partMenuData?.part_number || non3DPartNumber}</b> is not available to view in 3D Model.
                        </Typography>
                    </Box>
                )}



                {/* Loading / error overlay (blocks interaction only while visible) */}
                {(loading || error) && (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            zIndex: 999,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            background: (theme) =>
                                theme.palette.mode === 'dark'
                                    ? 'rgba(0,0,0,0.8)'
                                    : 'rgba(255,255,255,1)',
                        }}
                    >
                        {error ? (
                            <Box sx={containerStyles}>
                                <Typography
                                    color="error"
                                    variant="h6"
                                    mb={1}
                                >
                                    {error}
                                </Typography>
                                <Typography variant="body2">
                                    Please try refreshing the page or going
                                    back.
                                </Typography>
                            </Box>
                        ) : (
                                <LoadingBoxes
                                    text={loadingMessages[loadingStep]}
                                    subText="Note: The 3D model may take up to a minute to fully load."
                                    percentage={Math.round(percentage)}
                                />
                        )}
                    </Box>
                )}

                {/* Floating UI overlays – only actual boxes have pointerEvents:auto */}
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: 'none',
                    }}
                >
                    {/* Title (top center) */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 16,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 10,
                            pointerEvents: 'auto',
                        }}
                    >
                        <PageTitle
                            title={`${machineNumber || ''} | ${machineName || ''
                                }`}
                        />
                    </Box>

                    {/* Left menu toggle + panel */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 16,
                            left: 24,
                            display: 'flex',
                            flexDirection: 'column',
                            rowGap: 2,
                            pointerEvents: 'auto',
                        }}
                    >
                        <Tooltip
                            title={showMenuBox ? 'Close Menu' : 'Open Menu'}
                            arrow
                            placement="right"
                        >
                            <Button
                                sx={{
                                    background: showMenuBox
                                        ? (theme) =>
                                            theme.palette.primary.main
                                        : '#fff',
                                    color: !showMenuBox
                                        ? (theme) =>
                                            theme.palette.primary.main
                                        : '#fff',
                                }}
                                variant="modelMenuButtons"
                                onClick={() =>
                                    setShowMenuBox((prev) => !prev)
                                }
                            >
                                <MenuIcon />
                            </Button>
                        </Tooltip>

                        {showMenuBox && (
                            <Box
                                sx={{
                                    height: '100%',
                                    minHeight: '80vh',
                                    maxHeight: '80vh',
                                    display: 'flex',
                                    width: '450px',
                                    flexDirection: 'column',
                                    rowGap: 0,
                                    backgroundColor:
                                        'rgba(255, 255, 255, 0.8)',
                                    boxShadow:
                                        '0 0 6px 1px rgba(0,0,0,0.1)',
                                    borderRadius: '10px',
                                    overflow: 'hidden',
                                }}
                            >
                                <Box sx={{ minHeight: '38px', width: '100%' }}>
                                    <Tabs
                                        value={tabValue}
                                        onChange={handleTabChange}
                                        variant="fullWidth"
                                        textColor="primary"
                                        indicatorColor="primary"
                                        sx={{
                                            borderBottom:
                                                '1px solid rgba(0,0,0,0.1)',
                                        }}
                                    >
                                        <Tab label={menuTabLabels.subAssemblies} />
                                        <Tab label={menuTabLabels.rspl} />
                                        <Tab label={menuTabLabels.partsTree} />
                                    </Tabs>
                                </Box>

                                {/* Sub-Assemblies */}
                                {tabValue === 0 && (
                                    <TabPanel
                                        value={tabValue}
                                        index={0}
                                        title={menuTabLabels.subAssemblies}
                                        subtitle={showMenuTabSubtitles ? "Level 1 subassemblies from the assembly tree." : ""}
                                        headerActions={
                                            <Tooltip title="Scroll to Top">
                                                <IconButton
                                                    size="small"
                                                    onClick={scrollSubAssembliesToTop}
                                                >
                                                    <VerticalAlignTop fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        }
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                rowGap: 1.5,
                                                width: '100%',
                                                flex: 1,
                                                minHeight: 0,
                                            }}
                                        >
                                            <SearchInput
                                                value={subSearchTerm}
                                                onChange={(e) =>
                                                    setSubSearchTerm(
                                                        e.target.value
                                                    )
                                                }
                                                onSubmit={() =>
                                                    setSubSearchTerm((v) => v)
                                                }
                                            />

                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: 'text.secondary',
                                                    fontSize: '10px',
                                                }}
                                            >
                                                Click a subassembly to hide all
                                                others and show only that
                                                subassembly. Use Ctrl / Cmd +
                                                click to multi-select.
                                            </Typography>
                                            <Box
                                                ref={subAssembliesScrollRef}
                                                sx={{
                                                    flex: 1,
                                                    minHeight: 0,
                                                    overflowY: 'auto',
                                                    p: 1,
                                                }}
                                            >

                                                {filteredSubAssemblies.length ===
                                                    0 && (
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: 'text.secondary',
                                                                fontSize: 12,
                                                            }}
                                                        >
                                                            No subassemblies found.
                                                        </Typography>
                                                    )}

                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 1,
                                                    }}
                                                >
                                                    {filteredSubAssemblies.map(
                                                        ({ id, node }) => {
                                                            const isActive =
                                                                selectedSubAssemblyIds.includes(
                                                                    id
                                                                );
                                                            const matched = findMatchingProductForPart(
                                                                node?.name,
                                                                node?.productRef
                                                            );
                                                            return (
                                                                <Box
                                                                    onClick={(e) => handleSubAssemblyCardClick(e, id, node)}
                                                                    sx={{ cursor: "pointer" }}
                                                                >

                                                                    <PartCard
                                                                        data={{
                                                                            part_name: matched?.product_name || node.name,
                                                                            part_number: matched?.product_number ?? node.name ?? "N/A",
                                                                            purchasable: matched?.purchasable ?? 0,
                                                                            price: matched ? getSitePrice(matched) : null,
                                                                            productImageURL: matched?.productImageURL ?? null

                                                                        }}
                                                                        isActive={selectedSubAssemblyIds.includes(id)}
                                                                    />

                                                                </Box>

                                                            );
                                                        }
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </TabPanel>
                                )}

                                {/* RSPL Parts */}
                                {tabValue === 1 && (
                                    <TabPanel
                                        value={tabValue}
                                        index={1}
                                        title={menuTabLabels.rspl}
                                        subtitle={showMenuTabSubtitles ? "List RSPL parts from products with their mapped types." : ""}
                                        headerActions={
                                            <Tooltip title="Scroll to Top">
                                                <IconButton
                                                    size="small"
                                                    onClick={scrollRsplToTop}
                                                >
                                                    <VerticalAlignTop fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        }
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                rowGap: 1.5,
                                                width: '100%',
                                                flex: 1,
                                                minHeight: 0,
                                            }}
                                        >
                                            <SearchInput
                                                value={rsplSearchTerm}
                                                onChange={(e) => setRsplSearchTerm(e.target.value)}
                                                onSubmit={handleRsplSearchSubmit}
                                            />


                                            <Box
                                                ref={rsplScrollRef}
                                                sx={{
                                                    flex: 1,
                                                    minHeight: 0,
                                                    overflowY: 'auto',
                                                    px: 1,
                                                }}
                                            >
                                                {rsplLoading && (
                                                    <Box
                                                        sx={{
                                                            height: "300px",
                                                            display: "flex",
                                                            flexDirection: 'column',
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            width: '100%',
                                                            rowGap: 2
                                                        }}
                                                    >
                                                        <CircularProgress size={40} />
                                                        <Typography variant='body1' color='text.secondary'>Loading RSPL Parts...</Typography>
                                                    </Box>
                                                )}
                                                {rsplReady && (<>
                                                    {/* INNER TABS FOR PRODUCT TYPES */}
                                                    <Tabs
                                                        value={innerTab}
                                                        onChange={(e, v) => setInnerTab(v)}
                                                        sx={{ borderBottom: 1, borderColor: "divider", mb: 0.75 }}
                                                    >
                                                        {["All", ...productTypes].map((type, idx) => (
                                                            <Tab key={idx} label={type} value={type} />
                                                        ))}
                                                    </Tabs>


                                                    {["All", ...productTypes].map((type, idx) => (
                                                        <Box key={idx} role="tabpanel" hidden={innerTab !== type}>
                                                            {innerTab === type && (
                                                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                                                    {rsplRenderedByType[type]}
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    ))}


                                                </>)}



                                            </Box>
                                            <Box
                                                sx={{
                                                    mt: 2,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    columnGap: 2,
                                                    color: "text.secondary",
                                                    fontSize: "11px"
                                                }}
                                            >

                                                {productTypes.map((type, idx) => (

                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        sx={{ mb: 1 }}
                                                        onClick={() => handleAddAllProductType(type)}
                                                    >
                                                        Add All {type} Parts
                                                    </Button>


                                                ))}
                                            </Box>



                                        </Box>
                                    </TabPanel>
                                )}

                                {/* Parts Tree */}
                                {tabValue === 2 && (
                                    <TabPanel
                                        value={tabValue}
                                        index={2}
                                        title={menuTabLabels.partsTree}
                                        subtitle={showMenuTabSubtitles ? "Assembly tree derived from model." : ""}
                                        headerActions={
                                            <>
                                                <Tooltip title="Expand All">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={expandAllTreeNodes}
                                                        >
                                                            <FiChevronsDown size={18} />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Collapse All">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={collapseAllTreeNodes}
                                                        >
                                                            <FiChevronsUp size={18} />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Scroll to Top">
                                                    <IconButton
                                                        size="small"
                                                        onClick={scrollPartsTreeToTop}
                                                    >
                                                        <VerticalAlignTop fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        }
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                rowGap: 1.5,
                                                width: '100%',
                                                flex: 1,
                                                minHeight: 0,
                                            }}
                                        >
                                            <SearchInput
                                                value={treeSearchTerm}
                                                onChange={(e) =>
                                                    setTreeSearchTerm(
                                                        e.target.value
                                                    )
                                                }
                                                onSubmit={
                                                    handleTreeSearchSubmit
                                                }
                                                placeholder={`Search in ${menuTabLabels.partsTree}`}
                                            />
                                            <Box
                                                ref={partsTreeScrollRef}
                                                sx={{
                                                    flex: 1,
                                                    minHeight: 0,
                                                    overflowY: 'auto',
                                                }}
                                            >
                                                {assemblyTree ? (
                                                    renderTreeItems(
                                                        assemblyTree,
                                                        0
                                                    )
                                                ) : (
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: 'text.secondary',
                                                            mt: 2,
                                                            textAlign:
                                                                'center',
                                                        }}
                                                    >
                                                        Assembly tree not
                                                        loaded yet.
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </TabPanel>
                                )}
                            </Box>
                        )}
                    </Box>

                    {/* Top-right close (just a placeholder button for now) */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 16,
                            right: 24,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            pointerEvents: 'auto',
                        }}
                    >
                        <Tooltip title="Refresh Page">
                            <IconButton onClick={handleHardRefresh}>
                                <Autorenew />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={t("UI_TEXT.TOOLTIP_CLOSE_MODEL_VIEW")}>
                            <IconButton onClick={() => { handleCloseModel() }}>
                                <Close />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {/* Bottom-left disclaimer */}
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 10,
                            left: 10,
                            maxWidth: "100%",
                            pointerEvents: 'auto',
                        }}
                    >
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: "11px" }}
                        >
                            Disclaimer: "Some drawings may contain parts that may not appear on the machine 2D or 3D model"
                        </Typography>
                    </Box>

                    {/* Bottom-center toolbar + explode slider */}
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 25,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 10,
                            pointerEvents: 'auto',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            {/* Explode slider */}
                            {explodeEnabled && (
                                <Box
                                    sx={{
                                        px: 2,
                                        py: 1,
                                        borderRadius: '999px',
                                        backgroundColor:
                                            'rgba(255,255,255,0.9)',
                                        boxShadow:
                                            '0 0 6px 1px rgba(0,0,0,0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        columnGap: 2,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{ minWidth: 60 }}
                                    >
                                        Explode
                                    </Typography>
                                    <Slider
                                        value={explodeValue}
                                        min={0}
                                        max={1}
                                        step={0.05}
                                        onChange={(_, val) => {
                                            setExplodeValue(val);
                                            setSettings((prev) => ({
                                                ...prev,
                                                explodeValue: val,
                                            }));
                                        }}
                                        sx={{ width: 200 }}
                                    />
                                </Box>
                            )}

                            {/* Toolbar row */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: 2.5,
                                    flexWrap: 'wrap',
                                }}
                            >
                                {/* Group 1: Home */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Tooltip title={t("UI_TEXT.TOOLTIP_HOME_RESET_VIEW")}>
                                        <Button variant="modelMenuButtons" onClick={resetModel}>
                                            <HomeIcon />
                                        </Button>
                                    </Tooltip>
                                </Box>

                                {/* Group 2: Navigation + explode + zoom selected */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Tooltip title={navMode === "pan" ? "Rotate Mode" : "Pan Mode"}>
                                        <Button
                                            variant="modelMenuButtons"
                                            onClick={() => {
                                                const nextMode = navMode === "pan" ? "rotate" : "pan";
                                                setNavMode(nextMode);
                                                setSettings((prev) => ({ ...prev, navMode: nextMode }));
                                            }}
                                            sx={(theme) =>
                                                navMode === "pan"
                                                    ? {
                                                        backgroundColor: theme.palette.primary.main,
                                                        color: "#fff",
                                                    }
                                                    : {}
                                            }
                                        >
                                            {navMode === "pan" ? <Loop /> : <OpenWithIcon />}
                                        </Button>
                                    </Tooltip>

                                    <Tooltip title={explodeEnabled ? "Disable Explode Model" : "Explode Model"}>
                                        <Button
                                            variant="modelMenuButtons"
                                            onClick={() =>
                                                setExplodeEnabled((prev) => {
                                                    const next = !prev;
                                                    setSettings((settingsPrev) => ({
                                                        ...settingsPrev,
                                                        explodeEnabled: next,
                                                    }));
                                                    return next;
                                                })
                                            }
                                            sx={(theme) =>
                                                explodeEnabled
                                                    ? {
                                                        backgroundColor: theme.palette.primary.main,
                                                        color: '#fff',
                                                    }
                                                    : {}
                                            }
                                        >
                                            <OpenInFull />
                                        </Button>
                                    </Tooltip>

                                    <Tooltip title={selectedPart ? "Zoom to Selected" : "Zoom to Selected (select a part first)"}>
                                        <span>
                                            <Button
                                                variant="modelMenuButtons"
                                                disabled={!selectedPart}
                                                onClick={() => sceneRef.current?.zoomToSelectionToggle?.()}
                                            >
                                                <ZoomInIcon />
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Box>

                                {/* Group 3: Text toggles */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Tooltip title={machineTransparentToggle ? "Switch to Machine Solid" : "Switch to Machine Transparent"}>
                                        <Button
                                            variant="modelMenuButtons"
                                            onClick={handleMachineTransparencyToggle}
                                            sx={(theme) => ({
                                                width: 'auto',
                                                minWidth: 'fit-content',
                                                px: 1.5,
                                                borderRadius: '15px',
                                                textTransform: "none",
                                                whiteSpace: 'nowrap',
                                                overflow: 'visible',
                                                gap: 0.5,
                                                ...(machineTransparentToggle
                                                    ? {
                                                        backgroundColor: theme.palette.primary.main,
                                                        color: "#fff",
                                                    }
                                                    : {}),
                                            })}
                                        >
                                            Machine
                                            {machineTransparentToggle ? <BsTransparency size={14} /> : <PiDropSimpleFill size={14} />}
                                        </Button>
                                    </Tooltip>

                                    <Tooltip
                                        title={
                                            !selectedPart
                                                ? "Part transparency requires a selected part"
                                                : (partTransparencyToggle ? "Switch selected part to Solid" : "Switch selected part to Transparent")
                                        }
                                    >
                                        <span>
                                            <Button
                                                variant="modelMenuButtons"
                                                disabled={!selectedPart}
                                                onClick={handlePartTransparencyToggle}
                                                sx={(theme) => ({
                                                    width: 'auto',
                                                    minWidth: 'fit-content',
                                                    px: 1.5,
                                                    borderRadius: '15px',
                                                    textTransform: "none",
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'visible',
                                                    gap: 0.5,
                                                    ...(partTransparencyToggle
                                                        ? {
                                                            backgroundColor: theme.palette.primary.main,
                                                            color: "#fff",
                                                        }
                                                        : {}),
                                                })}
                                            >
                                                Part
                                                {partTransparencyToggle ? <BsTransparency size={14} /> : <PiDropSimpleFill size={14} />}
                                            </Button>
                                        </span>
                                    </Tooltip>

                                    <Tooltip
                                        title={
                                            !selectedPart
                                                ? "Isolate requires a selected part"
                                                : (isolateSelectionToggle ? "Disable Isolate" : "Isolate selected part")
                                        }
                                    >
                                        <span>
                                            <Button
                                                variant="modelMenuButtons"
                                                disabled={!selectedPart}
                                                onClick={handleIsolateToggle}
                                                endIcon={<NorthEast fontSize="small" />}
                                                sx={(theme) => ({
                                                    width: 'auto',
                                                    minWidth: 'fit-content',
                                                    px: 1.5,
                                                    borderRadius: '15px',
                                                    textTransform: "none",
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'visible',
                                                    ...(isolateSelectionToggle
                                                        ? {
                                                            backgroundColor: theme.palette.primary.main,
                                                            color: "#fff",
                                                        }
                                                        : {}),
                                                })}
                                            >
                                                Isolate
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Box>

                                {/* Group 4: Selection + parent */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Tooltip title={selectVisibleActive ? "Unselect Visible Parts" : "Select Visible Parts"}>
                                        <span>
                                            <Button
                                                variant="modelMenuButtons"
                                                onClick={handleSelectVisibleParts}
                                                sx={(theme) =>
                                                    selectVisibleActive
                                                        ? {
                                                            backgroundColor: theme.palette.primary.main,
                                                            color: "#fff",
                                                        }
                                                        : {}
                                                }
                                            >
                                                <CheckBoxIcon />
                                            </Button>
                                        </span>
                                    </Tooltip>

                                    <Tooltip title={selectedPart ? "Show Parent in View" : "Show Parent requires a selected part"}>
                                        <span>
                                            <Button
                                                variant="modelMenuButtons"
                                                disabled={!selectedPart}
                                                onClick={showParentFromSelection}
                                            >
                                                <KeyboardDoubleArrowUp />
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Box>

                                {/* Group 5: Help + more */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Tooltip title={t("UI_TEXT.TOOLTIP_HELP")}>
                                        <Button variant="modelMenuButtons" onClick={handleHelpClick}>
                                            <QuestionMark />
                                        </Button>
                                    </Tooltip>

                                    <Tooltip title={t("UI_TEXT.TOOLTIP_MORE")}>
                                        <Button variant="modelMenuButtons" onClick={handleMoreClick}>
                                            <MoreVertIcon />
                                        </Button>
                                    </Tooltip>
                                </Box>

                                    <Menu
                                        anchorEl={moreAnchorEl}
                                        open={isMoreMenuOpen}
                                        onClose={handleMoreClose}
                                        anchorOrigin={{
                                            vertical: 'top',
                                            horizontal: 'left',
                                        }}
                                        transformOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'left',
                                        }}
                                        sx={{ mt: -1 }}
                                    >
                                        {/* <MenuItem
                                            onClick={() => setUpAxis('Z_UP')}
                                            selected={upAxis === 'Z_UP'}
                                        >
                                            Z Up
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() => setUpAxis('Y_UP')}
                                            selected={upAxis === 'Y_UP'}
                                        >
                                            Y Up
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() => setUpAxis('X_UP')}
                                            selected={upAxis === 'X_UP'}
                                        >
                                            X Up
                                        </MenuItem> */}

                                        <MenuItem onClick={handleBom}>
                                            <ListItemIcon>
                                                <TableChartIcon fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText>BOM</ListItemText>
                                        </MenuItem>
                                        {/* <MenuItem onClick={handleSettings}>
                                            <ListItemIcon>
                                                <SettingsIcon fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText>
                                                Settings
                                            </ListItemText>
                                        </MenuItem> */}
                                    </Menu>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* Cart drawer */}
                    {cartOpen && (
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 25 + 60,
                                right: 35,
                                zIndex: 25,
                                height: '100%',
                                minHeight: '75vh',
                                maxHeight: '75vh',
                                display: 'flex',
                                width: '500px',
                                flexDirection: 'column',
                                rowGap: 0,
                                backgroundColor:
                                    'rgba(255, 255, 255, 0.8)',
                                boxShadow: '0 0 6px 1px rgba(0,0,0,0.1)',
                                borderRadius: '10px',
                                overflow: 'hidden',
                                p: 2,
                                pointerEvents: 'auto',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>

                                <Box>
                            <Typography
                                fontWeight="bold"
                                variant="body1"
                                mb={0}
                            >
                                Cart
                            </Typography>
                            <Typography
                                variant="body2"
                                fontSize="11px"
                                mb={1}
                            >
                                List of all the Parts in Cart
                            </Typography>
                                </Box>
                                <Button
                                    variant="text"
                                    color="warning"
                                    onClick={handleClearCart}
                                >
                                    Clear Cart
                                </Button>
                            </Box>
                            <Box
                                sx={{
                                    flexGrow: 1,
                                    overflowY: 'auto',
                                    minHeight: '200px',
                                    p: 1,
                                }}
                            >
                                <Table size="small" sx={{ fontSize: 12 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontSize: 12 }}><b>{t('UI_TEXT.PART')}</b></TableCell>
                                            <TableCell sx={{ fontSize: 12 }} align="center"><b>Qty</b></TableCell>
                                            <TableCell sx={{ fontSize: 12 }}><b>Price</b></TableCell>
                                            <TableCell sx={{ fontSize: 12 }}><b>Action</b></TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {cartItems.map((item) => (
                                            <TableRow key={item.part_number} sx={{ fontSize: 12 }}>

                                                <TableCell sx={{ maxWidth: 150, fontSize: 12 }}>
                                                    <Typography
                                                        noWrap
                                                        sx={{
                                                            fontSize: 12,
                                                            textOverflow: "ellipsis",
                                                            overflow: "hidden"
                                                        }}
                                                    >
                                                        {item.part_number}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell sx={{ fontSize: 12 }}>
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            gap: 1,
                                                            fontSize: 12
                                                        }}
                                                    >
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => decrementQty(item.part_number)}
                                                            sx={{ fontSize: 12 }}
                                                        >
                                                            −
                                                        </IconButton>

                                                        <span style={{ fontSize: 12 }}>{item.qty}</span>

                                                        <IconButton
                                                            size="small"
                                                            onClick={() => incrementQty(item.part_number)}
                                                            sx={{ fontSize: 12 }}
                                                        >
                                                            +
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>

                                                <TableCell sx={{ fontSize: 12 }}>
                                                    {item.price}
                                                </TableCell>

                                                <TableCell sx={{ fontSize: 12 }}>
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => removeCartItem(item.part_number)}
                                                        sx={{ fontSize: 12 }}
                                                    >
                                                        <Delete sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </TableCell>

                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>



                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>

                                <Box>

                                    <Typography
                                        fontWeight="bold"
                                        variant="body1"
                                        mb={0}
                                    >
                                        Request List
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        fontSize="11px"
                                        mb={1}
                                    >
                                        List of all the Parts in Request List
                                    </Typography>
                                </Box>
                                <Button
                                    variant="text"
                                    color="warning"
                                    onClick={handleClearRequestList}
                                >
                                    Clear Request List
                                </Button>
                            </Box>
                            <Box
                                sx={{
                                    flexGrow: 1,
                                    overflowY: 'auto',
                                    minHeight: '200px',
                                    p: 1,
                                }}
                            >

                                <Table size="small" sx={{ fontSize: 12 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontSize: 12 }}><b>{t('UI_TEXT.PART')}</b></TableCell>
                                            <TableCell sx={{ fontSize: 12 }}><b>Action</b></TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {requestItems.map((item) => (
                                            <TableRow key={item.part_number} sx={{ fontSize: 12 }}>

                                                <TableCell sx={{ maxWidth: 170, fontSize: 12 }}>
                                                    <Typography
                                                        noWrap
                                                        sx={{
                                                            fontSize: 12,
                                                            textOverflow: "ellipsis",
                                                            overflow: "hidden"
                                                        }}
                                                    >
                                                        {item.part_number}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell sx={{ fontSize: 12 }}>
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => removeRequestItem(item.part_number)}
                                                        sx={{ fontSize: 12 }}
                                                    >
                                                        <Delete sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </TableCell>

                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>


                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                    alignItems: 'center',
                                    mb: 1.25,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-start',
                                        columnGap: 1.25,
                                        alignItems: 'center',
                                    }}
                                >
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            setCartOpen(false);
                                        }}
                                    >
                                    {t("BUTTON.CLOSE")}

                                    </Button>


                                </Box>
                                <Button variant="contained" onClick={handleOrderItems} disabled={orderingItems}>
                                    {orderingItems ? "Processing..." : "Order Items"}
                                </Button>
                            </Box>
                            <Typography
                                variant="body2"
                                fontSize="10px"
                                color="text.secondary"
                            >
                                <b>Note:</b> The Items in the above Cart /
                                Request List are specific to this model.
                            </Typography>
                        </Box>
                    )}

                    {/* Cart FAB */}
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 25,
                            right: 35,
                            zIndex: 10,
                            pointerEvents: 'auto',
                        }}
                    >
                        <Badge
                            badgeContent={cartItems.length}
                            color="error"
                            showZero
                            overlap="circular"
                        >
                            <Box
                                onClick={handleCartClick}
                                sx={{
                                    width: 50,
                                    height: 50,
                                    bgcolor: (theme) =>
                                        theme.palette.primary.main,
                                    color: '#fff',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        bgcolor: (theme) =>
                                            theme.palette.primary.dark,
                                        transform: 'scale(1.05)',
                                    },
                                    '&:active': {
                                        transform: 'scale(0.95)',
                                    },
                                }}
                            >
                                <ShoppingCart />
                            </Box>
                        </Badge>
                    </Box>

                    {/* Part menu (draggable) */}
                    {showPartMenu && partMenuData && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 80,
                                right: 30,
                                zIndex: 30,
                                transform: `translate(${partMenuDelta.x}px, ${partMenuDelta.y}px)`,
                                pointerEvents: 'auto',
                            }}
                        >
                            <PartMenu
                                onHeaderMouseDown={handlePartMenuMouseDown}
                                data={partMenuData}
                                onClose={() => setShowPartMenu(false)}
                                onShowParentClick={showParentFromSelection}
                                onIsolateClick={() => {
                                    setIsolateSelectionToggle(true);
                                }}
                                occurrenceToggle={occurrenceToggle}
                                onOccurrenceToggle={handleOccurrenceToggle}
                                isInCart={isInCart}
                                isInRequest={isInRequest}
                                addToCart={addToCart}
                                addToRequestList={addToRequestList}
                            />

                        </Box>
                    )}

                    {/* Multiple parts menu */}
                    {showMultiPartMenu && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 80,
                                right: 30,
                                zIndex: 29,
                                pointerEvents: 'auto',
                                transform: `translate(${multiPartMenuDelta.x}px, ${multiPartMenuDelta.y}px)`,
                            }}
                        >
                            <MultiplePartsMenu
                                count={multiPartSelection.length}
                                items={multiPartSelection}
                                machineId={machine_id}
                                onHeaderMouseDown={handleMultiPartMenuMouseDown}
                                onClose={() => setShowMultiPartMenu(false)}
                                onAddToCart={handleMultipleAddToCart}
                                findMatchingProductForPart={findMatchingProductForPart}
                                getSitePrice={getSitePrice}
                            />

                        </Box>
                    )}
            </Box>

            {/* Settings Modal */}
            <Dialog
                open={settingsOpen}
                onClose={handleSettingsClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        borderRadius: 2,
                        outline: 'none',
                    },
                }}
            >
                <DialogTitle>Model Settings</DialogTitle>
                <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

                    {/* Default Navigation Mode */}
                    <Box>
                        <Typography fontWeight={600}>Default Navigation Mode</Typography>
                        <RadioGroup
                            value={settings.navMode}
                            onChange={(e) =>
                                setSettings((prev) => ({ ...prev, navMode: e.target.value }))
                            }
                            row
                        >
                            <FormControlLabel value="rotate" control={<Radio />} label="Rotate" />
                            <FormControlLabel value="pan" control={<Radio />} label="Pan" />
                        </RadioGroup>
                    </Box>

                    {/* Transparency toggle */}
                    <Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={settings.transparentOnSelect}
                                    onChange={(e) =>
                                        setSettings((prev) => ({
                                            ...prev,
                                            transparentOnSelect: e.target.checked,
                                        }))
                                    }
                                />
                            }
                            label="Make part transparent when selected"
                        />
                    </Box>

                    {/* Explode Mode */}
                    <Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={settings.explodeEnabled}
                                    onChange={(e) =>
                                        setSettings((prev) => ({
                                            ...prev,
                                            explodeEnabled: e.target.checked,
                                        }))
                                    }
                                />
                            }
                            label="Enable Explode Mode"
                        />

                        {settings.explodeEnabled && (
                            <Slider
                                value={settings.explodeValue}
                                min={0}
                                max={1}
                                step={0.05}
                                onChange={(_, value) =>
                                    setSettings((prev) => ({ ...prev, explodeValue: value }))
                                }
                                sx={{ width: 200, mt: 1 }}
                            />
                        )}
                    </Box>

                    {/* Auto Zoom */}
                    <Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={settings.autoZoom}
                                    onChange={(e) =>
                                        setSettings((prev) => ({
                                            ...prev,
                                            autoZoom: e.target.checked,
                                        }))
                                    }
                                />
                            }
                            label="Auto-zoom to selected part"
                        />
                    </Box>

                    {/* Gizmo Visibility */}
                    <Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={settings.showGizmo}
                                    onChange={(e) =>
                                        setSettings((prev) => ({
                                            ...prev,
                                            showGizmo: e.target.checked,
                                        }))
                                    }
                                />
                            }
                            label="Show 3D Navigation Gizmo"
                        />
                    </Box>

                    {/* Light Intensity */}
                    <Box>
                        <Typography fontWeight={600}>{t("LABELS.LIGHT_INTENSITY")}</Typography>
                        <Slider
                            value={settings.light}
                            min={0.2}
                            max={2}
                            step={0.1}
                            onChange={(_, value) =>
                                setSettings((prev) => ({ ...prev, light: value }))
                            }
                            sx={{ width: 200, mt: 1 }}
                        />
                    </Box>

                </DialogContent>

                <DialogActions>
                    <Button onClick={handleSettingsClose}>{t("BUTTON.CLOSE")}</Button>
                    <Button
                        onClick={handleSettingsClose}
                        variant="contained"
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* BOM Modal */}
            <ModalWrapper
                open={bomOpen}
                setOpen={setBomOpen}
                onClose={handleBomClose}
                title="BOM (Bill of Materials)"
                subtitle="Bill of Materials extracted from the 3D model"
                width="70vw"
                content={
                    <Datagrid
                        data={bomData}
                        columns={bomColumns}
                        rowIdField="id"
                        showSearchBar={true}
                        autoHeight={false}
                        gridHeight="60vh"
                        pageSize={5}
                    />
                }
                actions={
                    <>
                        <Button variant="outlined" onClick={handleBomClose}>
                            {t("BUTTON.CLOSE")}

                        </Button>
                    </>
                }
            />

            <ModalWrapper
                open={helpOpen}
                setOpen={setHelpOpen}
                onClose={() => setHelpOpen(false)}
                title="Model Viewer Help"
                subtitle="Quick reference for the icons and controls."
                width="70vw"
                content={helpContent}
                actions={
                    <>
                        <Button
                            variant="contained"
                            startIcon={<SupportAgent />}
                            onClick={handleContactCcrClick}
                        >
                            {t('LABELS.CONTACT_CCR')}
                        </Button>
                        <Button variant="contained" onClick={() => setHelpOpen(false)}>
                            {t("BUTTON.CLOSE")}
                        </Button>
                    </>
                }
            />

            <ContactCCRForm
                open={ccrOpen}
                setOpen={setCcrOpen}
                machine={machine}
                prefill={ccrPrefill}
            />

        </>
    );
};

export default ModelViewer;
